import React, { useState, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { createPortal } from 'react-dom';
import ChannelTypeIcon from '@/components/icons/ChannelTypeIcon';
import { ChannelThumbnail } from '@/components/ChannelThumbnail';
import { ColumnUpdatedAt } from '@/components/ColumnUpdatedAt';
import AdTypeBadge from '@/components/AdTypeBadge';
import Tooltip from '@/components/Tooltip';
import ChannelVerifiedBadge from '@/components/ChannelVerifiedBadge';
import CountryFlag from '@/components/CountryFlag';
import { useI18nText } from '@/lib/utils/i18nText';
import { ceilWithCommission, formatAbbrev, formatLabelText } from '@/lib/utils/misc';
import { formatLocalDateTime, relativeDate } from '@/lib/utils/timezone';
import {
  getChannelTypeColor,
  getChannelTypeFollowerField,
  getChannelTypeMetricIcon,
  getChannelTypeMetricLabel,
  getChannelTypeSocialUrl,
  getChannelTypeTitle,
} from '@/lib/utils/channelPlatformMeta';
import { EmptyDash, formatNum, getRowStripeClass, getInfluencerEmails, titleOf, percentOf } from '../utils';
import { SOCIAL_PLATFORM_ENTRIES } from '../constants';
import { CAMPAIGN_INFLUENCER_STATUSES, getInfluencerStatusBadge } from '@/lib/constants/influencerStatuses';
import { RowActionButton } from './RowActionButton';
import { GgcBadge } from '@/components/ui/ggc/badge';
import { TbSubtask } from "react-icons/tb";
import { CampaignHistoryButton, buildCampaignHistoryItems, type CampaignHistoryItem, type CampaignHistoryChannelInfo } from '@/components/CampaignHistoryPopover';
import { CHANNEL_QUICK_BUTTON_META } from '@/components/ChannelNameQuickButtons';
import { ChannelNameWithPopover } from './ChannelNameWithPopover';
import { getChannelHandle } from '@/lib/utils/channelHandle';
import { useNameColumnButtons } from '@/lib/utils/nameColumnButtons';
import { StatusIconPopover } from './StatusIconPopover';
import { RemovableTag } from './RemovableTag';
import {
  CollapsibleTagList,
  EstPriceCell,
  EmailCell,
  GeneralViewershipCell,
  EngagementCell,
  DetailedKPICell,
  LastVideoCell,
  VideoPostsList,
  IncCommentCell,
} from './cells';
import { type CommentDeleteTarget } from '@/components/MultiTargetCommentTag';
import type { Influencer, SiblingUser, Campaign, CampaignPost, KpiOption, ColumnDef, CpvEstimation } from '../types';
import { updateInfluencerPrice, updateInfluencerVideoType, saveCommentTokens } from '@/lib/api/campaign.api';
import { useAppSelector } from '@/store/hooks';
import { GgcButton } from '@/components/ui/ggc/button';
import { GgcInput } from '@/components/ui/ggc/input';
import { GgcSelect } from '@/components/ui/ggc/select';
import SingleSelect from '@/components/SingleSelect';
import { InclusionCommentEditor } from '@/components/InclusionCommentEditor';
import { TagEditPopover } from './TagEditPopover';

export function InfluencerRow({
  user, isAdmin, selected, onToggle, columns, inCampaign, inCampaignDeleted, inCampaignRejected,
  selectedUserIds, rowIndex, campaigns, selectedCampaignId, organizationId,
  kpiOption, onTagsSaved, onTagRemove, shortSubsFormat, legendScope = 'checkbox', showEmptyDash = true,
  onOpenPanelDrawer, onChatSystemDrawer, onChannelDetailsDrawer, onStatsDrawer, onChannelEditDrawer, perUserAnalysis,
  cpvEstimationByUserId,
  showEmptyMetricIcons = true,
  showChannelHandle = false,
  showColumnUpdatedAt = true,
  siblingsExpanded,
  onSiblingToggle,
}: {
  user: Influencer; isAdmin: boolean; selected: boolean;
  onToggle: (id: string) => void; columns: ColumnDef[]; inCampaign?: boolean; inCampaignDeleted?: boolean; inCampaignRejected?: boolean;
  selectedUserIds: Set<string>; rowIndex: number;
  campaigns: Campaign[];
  selectedCampaignId?: string;
  organizationId?: string;
  kpiOption?: KpiOption;
  onTagsSaved?: (userId: string, newTags: string[]) => void;
  onTagRemove?: (user: Influencer, tag: string) => Promise<void>;
  shortSubsFormat?: boolean;
  legendScope?: 'checkbox' | 'row';
  showEmptyDash?: boolean;
  showEmptyMetricIcons?: boolean;
  showChannelHandle?: boolean;
  showColumnUpdatedAt?: boolean;
  onOpenPanelDrawer?: (userId: string) => void;
  onChatSystemDrawer?: (userId: string) => void;
  onChannelDetailsDrawer?: (userId: string) => void;
  onStatsDrawer?: (userId: string) => void;
  onChannelEditDrawer?: (userId: string) => void | Promise<void>;
  perUserAnalysis?: Record<string, { posts?: CampaignPost[] }>;
  cpvEstimationByUserId?: Record<string, CpvEstimation>;
  siblingsExpanded?: boolean;
  onSiblingToggle?: (userId: string, expanded: boolean) => void;
}) {
  const { t } = useI18nText();
  // Select the raw array (a stable store reference) and derive the lookup map
  // with useMemo. Returning Object.fromEntries() directly from the selector
  // creates a new object every render, which trips Redux's reference-equality
  // check and forces an unnecessary rerender on every store update.
  const advertisingTypes = useAppSelector(
    (s) => s.envs.advertisingTypes as Array<{ _id?: string; id?: string; title?: string }>,
  );
  // The default/suggested comment (user.suggestedComment) is only shown to users with
  // `toAccessAllFeatures` — mirrors gc-mono ChannelsBlock, and the BE returns the field
  // to everyone, so the gate must live here.
  const canSeeSuggested = useAppSelector(
    (s) => !!(s.envs.currentUser as { permissions?: Record<string, boolean> } | null)?.permissions?.toAccessAllFeatures,
  );
  const advertisingTypesMap = useMemo(
    () => Object.fromEntries(advertisingTypes.map((at) => [at._id ?? at.id ?? '', at.title ?? ''])),
    [advertisingTypes],
  );
  // Which channel-links actions to surface as quick buttons under the channel name
  // (toggled from the checkboxes inside ChannelLinksPopover, persisted to localStorage).
  const nameColButtons = useNameColumnButtons();
  // Id of the row (main or sibling) whose edit dialog data is currently loading,
  // so the clicked edit quick-button shows a spinner.
  const [editLoadingId, setEditLoadingId] = useState<string | null>(null);
  const [showSiblingsLocal, setShowSiblingsLocal] = useState(false);
  const [, forceRowUpdate] = useReducer(x => x + 1, 0);
  // Use controlled state from parent if provided (to sync with list filtering), otherwise local state
  const showSiblings = siblingsExpanded ?? showSiblingsLocal;
  const toggleSiblings = () => {
    if (onSiblingToggle) onSiblingToggle(user._id, !showSiblings);
    else setShowSiblingsLocal(p => !p);
  };
  const collapseSiblings = () => {
    if (onSiblingToggle) onSiblingToggle(user._id, false);
    else setShowSiblingsLocal(false);
  };

  const stripeClass = getRowStripeClass(user);
  const siblings = Array.isArray(user.siblingUsers) ? user.siblingUsers : [];

  const fmtNum = (n?: number) => {
    if (!n) return '0';
    return shortSubsFormat ? formatAbbrev(n, true) : formatNum(n);
  };

  // row 모드일 때만 전체 행에 적용, 기본(checkbox)은 rowNum 셀에만
  const showLegendOnRow = legendScope === 'row';

  // 빈 데이터 셀 헬퍼
  const emptyTd = (colId: string) => (
    <td key={colId}>{showEmptyDash ? <EmptyDash /> : null}</td>
  );
  // 인라인용
  const ED = showEmptyDash ? <EmptyDash /> : null;

  // Channel-links shortcut buttons rendered as a row under the channel name.
  // Per-action visibility is controlled globally by the checkboxes in ChannelLinksPopover.
  // `history` (main row only) renders as the same square icon style, with the
  // campaign count shown as a small badge in its top-right corner.
  const renderNameColumnButtons = (
    inf: Influencer | SiblingUser,
    history?: { tooltip: string; items: CampaignHistoryItem[]; channel?: CampaignHistoryChannelInfo },
  ) => {
    const buttons: React.ReactElement[] = [];
    // Icon + tooltip come from the shared CHANNEL_QUICK_BUTTON_META (single source, shared with
    // ChannelNameQuickButtons). This row keeps its own <a> href fallback + drawer wiring.
    const navAnchor = (key: 'chat' | 'details' | 'stats', href: string, onDrawer?: (userId: string) => void) => {
      const meta = CHANNEL_QUICK_BUTTON_META[key];
      return (
        <Tooltip key={key} content={t(meta.tooltipEn, meta.tooltipKo)} side="bottom">
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={`actions-btn actions-btn--${key}`}
            onClick={(e) => {
              if (onDrawer) {
                e.preventDefault();
                onDrawer(inf._id);
              }
            }}
          >
            <i className={`fa ${meta.icon}`} />
          </a>
        </Tooltip>
      );
    };
    if (nameColButtons.chat) buttons.push(navAnchor('chat', `/influencer/${inf._id}/campaigns`, onChatSystemDrawer));
    if (nameColButtons.details) buttons.push(navAnchor('details', `/panel/user/${inf._id}`, onChannelDetailsDrawer));
    if (nameColButtons.stats) buttons.push(navAnchor('stats', `/influencer/${inf._id}/stats`, onStatsDrawer));
    if (nameColButtons.edit) {
      // Edit opens a modal dialog (no navigable URL), so render a button rather than a link.
      // Show a spinner while the dialog's data loads (the callback returns a Promise).
      const editLoading = editLoadingId === inf._id;
      const meta = CHANNEL_QUICK_BUTTON_META.edit;
      buttons.push(
        <Tooltip key="edit" content={t(meta.tooltipEn, meta.tooltipKo)} side="bottom">
          <button
            type="button"
            className="actions-btn actions-btn--edit"
            disabled={editLoading}
            onClick={async () => {
              if (!onChannelEditDrawer || editLoading) return;
              setEditLoadingId(inf._id);
              try {
                await onChannelEditDrawer(inf._id);
              } finally {
                setEditLoadingId(null);
              }
            }}
          >
            <i className={`fa ${editLoading ? 'fa-spinner fa-spin' : meta.icon}`} />
          </button>
        </Tooltip>
      );
    }
    if (history) {
      // Disabled (not hidden) when there's no history, per CLAUDE.md "숨김 최소화" — a
      // disabled trigger makes its Tooltip auto-essential, and the shared
      // `CampaignHistoryButton` handles both branches (see its own doc comment).
      buttons.push(
        <CampaignHistoryButton
          key="history"
          items={history.items}
          channel={history.channel}
          size="md"
          tooltip={history.tooltip}
          tooltipSide="bottom"
        />
      );
    }
    if (buttons.length === 0) return null;
    return <div className="influencer-name-actions-line"><div className="influencer-name-actions">{buttons}</div></div>;
  };

  const visibleCols = columns.filter((c) => c.visible);
  const renderCol = (colId: string, u: Influencer | SiblingUser = user, isSibling = false, siblingIndex?: number) => {
    const ch = u.channels?.[0];
    const name = u.profile?.fullname || u.profile?.name || ch?.title || 'Unknown';
    switch (colId) {
      case 'rowNum': {
        const campaignCellClass = user.isBlacklisted ? 'blacklisted-cell'
          : inCampaignDeleted ? 'in-campaign-deleted-cell'
          : inCampaign ? 'in-campaign-cell'
          : '';
        const cellExtras = [
          !showLegendOnRow && stripeClass ? stripeClass : '',
          !showLegendOnRow && campaignCellClass ? campaignCellClass : '',
        ].filter(Boolean).join(' ');
        const numCellCls = cellExtras ? `text-center text-ggc-text-gray text-xs w-[40px] ${cellExtras}` : 'text-center text-ggc-text-gray text-xs w-[40px]';

        // Outdated video / rejected-deleted / blacklisted each get their own icon
        // below the number (with a tooltip), so they stay visible even on
        // colorblind-unfriendly or low-contrast backgrounds. The number tooltip
        // keeps the one remaining state without its own icon (plain campaign membership).
        const legendLabels: string[] = [];
        if (inCampaign) legendLabels.push(t('Influencer is in selected campaign', '인플루언서가 선택된 캠페인에 포함됨'));

        const statusIcons: { key: string; icon: string; label: string }[] = [];
        if (stripeClass === 'stripe-yellow') statusIcons.push({ key: 'stripe-yellow', icon: 'fa-clock', label: t("Latest influencer's video dated more than a month ago", '인플루언서의 최신 영상이 1개월 이상 지남') });
        if (stripeClass === 'stripe-gray')   statusIcons.push({ key: 'stripe-gray', icon: 'fa-clock', label: t("Latest influencer's video dated more than a month ago (stats recently updated)", '인플루언서의 최신 영상이 1개월 이상 지남 (통계는 최근 갱신됨)') });
        if (inCampaignDeleted) {
          // Reuse the same icon as the influencer status column/filter (CAMPAIGN_INFLUENCER_STATUSES)
          // for 'rejected' so this badge stays visually consistent with that status elsewhere in the app.
          if (inCampaignRejected) {
            const rejectedIcon = CAMPAIGN_INFLUENCER_STATUSES.find((s) => s.value === 'rejected')?.icon ?? 'frown-o';
            statusIcons.push({ key: 'rejected', icon: `fa-${rejectedIcon}`, label: t('Influencer is rejected from selected campaign', '인플루언서가 선택된 캠페인에서 거절됨') });
          } else {
            statusIcons.push({ key: 'deleted', icon: 'fa-trash', label: t('Influencer is deleted from selected campaign', '인플루언서가 선택된 캠페인에서 삭제됨') });
          }
        }
        if (user.isBlacklisted) statusIcons.push({ key: 'blacklisted', icon: 'fa-ban', label: t('Blacklisted influencer', '블랙리스트 인플루언서') });

        const cellContent = isSibling
          ? <span className="sibling-row-num">{rowIndex}-{(siblingIndex ?? 0) + 1}</span>
          : rowIndex;

        return (
          <td key={colId} className={`${numCellCls}${isSibling ? ' sibling-row-num-td' : ''}`}>
            {isSibling && (
              <Tooltip content={t('Collapse sibling channels', '연결 채널 접기')}>
                <span
                  className="sibling-tree-clickzone"
                  onClick={() => collapseSiblings()}
                />
              </Tooltip>
            )}
            {legendLabels.length > 0 ? (
              <Tooltip content={legendLabels[0]}>
                <span>{cellContent}</span>
              </Tooltip>
            ) : cellContent}
            {statusIcons.length > 0 && (
              <div className="rownum-status-icons">
                {statusIcons.map((s) => (
                  <Tooltip key={s.key} content={s.label}>
                    <i className={`fa ${s.icon} rownum-status-icon rownum-status-icon--${s.key}`} />
                  </Tooltip>
                ))}
              </div>
            )}
          </td>
        );
      }
      case 'name': {
        // Computed once per row (main only — siblings don't carry their own campaign
        // history in this view) so the count can be shown both in the ChannelLinksPopover
        // "Campaign History" row and, when enabled, as the quick-button badge below.
        const historyItems = !isSibling ? buildCampaignHistoryItems(user.campaigns, campaigns) : [];
        return (
        <td key={colId} className={isSibling ? 'sibling-name-td' : undefined}>
          <div className="influencer-name-cell">
            <ChannelThumbnail
              src={ch?.thumbnail}
              onClick={(e) => { (e.currentTarget.closest('.influencer-name-cell') as HTMLElement)?.querySelector<HTMLElement>('.influencer-name-link')?.click(); }}
            />
            <div className="influencer-name-stack">
              <div className="influencer-name-row">
                <ChannelTypeIcon type={ch?.type ?? 'youtube'} size={12} asBadge />
                <ChannelNameWithPopover
                  userId={u._id}
                  name={name}
                  thumbnailUrl={ch?.thumbnail}
                  channelType={ch?.type}
                  channelUrl={ch?.url}
                  subtitle={'email' in u ? u.email : undefined}
                  campaignHistoryCount={historyItems.length > 0 ? historyItems.length : undefined}
                  onChatSystemDrawer={onChatSystemDrawer}
                  onChannelDetailsDrawer={onChannelDetailsDrawer}
                  onStatsDrawer={onStatsDrawer}
                  onChannelEditDrawer={onChannelEditDrawer}
                />
                <ChannelVerifiedBadge channel={ch} />
              </div>
              {showChannelHandle && (() => {
                const handle = getChannelHandle(ch);
                if (!handle) return null;
                return <span className="influencer-channel-handle">@{handle}</span>;
                // return ch?.url
                //   ? <a href={ch.url} target="_blank" rel="noopener noreferrer" className="influencer-channel-handle">@{handle}</a>
                //   : <span className="influencer-channel-handle">@{handle}</span>;

              })()}
              <div className="influencer-meta-row">
                {!isSibling && siblings.length > 0 && (() => {
                  // Distinct platform names of sibling channels, re-described as text in the tooltip.
                  const sibPlatforms = Array.from(
                    new Set(siblings.map((sib) => getChannelTypeTitle(sib.channels?.[0]?.type ?? 'youtube'))),
                  );
                  const siblingTooltip = t(
                    `Sibling channels (${siblings.length}): ${sibPlatforms.join(', ')}. Click to group and view these channels together.`,
                    `연결 채널 (${siblings.length}개): ${sibPlatforms.join(', ')}. 클릭하면 연결된 채널들을 묶어서 함께 볼 수 있습니다.`,
                  );
                  return (
                    <div className="sibling-tag-wrap">
                      <Tooltip content={siblingTooltip}>
                        <button type="button" className={`sibling-tag${showSiblings ? ' sibling-tag--open' : ''}`} onClick={toggleSiblings}>
                          <TbSubtask color='var(--text-muted)' size={14} />
                          <span className="sibling-tag__icons">
                            {siblings.slice(0, 3).map((sib, i) => (
                              <ChannelTypeIcon key={i} type={sib.channels?.[0]?.type ?? 'youtube'} size={11} asBadge badgeSize="sm" />
                            ))}
                          </span>
                          <span className="mr-1" />
                          {/* <span className="sibling-tag__count">{siblings.length}</span> */}
                        </button>
                      </Tooltip>
                    </div>
                  );
                })()}
              </div>
              {renderNameColumnButtons(
                u,
                nameColButtons.history
                  ? {
                      // Siblings never carry their own campaign history in this grouped
                      // view (SiblingUser has no `campaigns`), so their count is always 0
                      // and the button renders disabled — but with a reason distinct from
                      // a main row that genuinely has none, to avoid implying "no history".
                      tooltip: isSibling
                        ? t(
                            'Connected (sibling) channel — campaign history is not shown in this grouped list. Open the channel to view it.',
                            '연결(sibling) 채널입니다 — 이 묶음 목록에서는 캠페인 이력을 표시하지 않습니다. 채널을 열어 확인하세요.',
                          )
                        : historyItems.length > 0
                          ? t(
                              `Click to view the past campaign history that "${name}" has been part of.`,
                              `클릭하면 "${name}"의 과거 포함되었던 캠페인 이력을 볼 수 있습니다.`,
                            )
                          : t(
                              `"${name}" has no participated campaign history.`,
                              `"${name}"은(는) 참여된 캠페인 이력이 없습니다.`,
                            ),
                      items: historyItems,
                      channel: { thumbnail: ch?.thumbnail, type: ch?.type, name },
                    }
                  : undefined,
              )}
            </div>
          </div>
        </td>
        );
      }
      case 'tags': {
        const infTags = (u as Influencer).tags ?? [];
        return (
          <td key={colId} className="w-[340px] min-w-[340px] max-w-[340px]">
            {isAdmin ? (
              <div className="tags-cell flex flex-col items-start gap-1.5">
                <div className="label-list">
                  {infTags.length === 0 && showEmptyDash && <EmptyDash />}
                  {infTags.map((tg, i) => (
                    <RemovableTag
                      key={`${tg}-${i}`}
                      tag={tg}
                      onRemove={() => onTagRemove?.(u as Influencer, tg) ?? Promise.resolve()}
                    />
                  ))}
                </div>
                <TagEditPopover
                  user={u as Influencer}
                  campaigns={campaigns}
                  onSaved={(newTags) => onTagsSaved?.((u as Influencer)._id, newTags)}
                />
              </div>
            ) : showEmptyDash ? <EmptyDash /> : null}
          </td>
        );
      }
      case 'subs': {
        const onlineSub = u.stats?.allTime?.onlineSubscribers;
        const subs = u.stats?.allTime?.subscribers;
        const parts = [onlineSub, subs].filter((n): n is number => typeof n === 'number').map((n) => fmtNum(n));
        const text = parts.length > 0 ? parts.join(' / ') : '—';
        const tooltipParts: string[] = [];
        if (onlineSub != null) tooltipParts.push(`${t('Online subscribers', '온라인 구독자')}: ${fmtNum(onlineSub)}`);
        if (subs != null) tooltipParts.push(`${t('Total subscribers', '전체 구독자')}: ${fmtNum(subs)}`);
        const tooltipContent = tooltipParts.length > 0 ? tooltipParts.join(' / ') : null;
        return (
          <td key={colId} className="text-right whitespace-nowrap tabular-nums">
            {tooltipContent ? (
              <Tooltip essential content={tooltipContent}>
                <span className="socials-stats-number">{text}</span>
              </Tooltip>
            ) : (
              // No subscriber data at all — use the shared EmptyDash instead of a plain
              // '—' span so the color matches every other empty cell in the table
              // (`.socials-stats-number`'s dark text color is meant for real numbers).
              showEmptyDash ? <EmptyDash /> : null
            )}
            {/* Subscribers also come from the channel stats crawl (statsUpdatedAt) — show the
                same "Updated At" freshness line as the viewership/last-activity columns. */}
            {showColumnUpdatedAt && <ColumnUpdatedAt iso={(u as Influencer).statsUpdatedAt} />}
          </td>
        );
      }
      case 'keywords': {
        const kws = isSibling ? [] : ((user as Influencer).keywords ?? []);
        if (kws.length === 0) return emptyTd(colId);
        return (
          <td key={colId} className="max-w-[340px] min-w-[220px]">
            <CollapsibleTagList
              items={kws}
              renderItem={(k, i) => <span key={`${k}-${i}`} className="ggc-tag">{k}</span>}
            />
          </td>
        );
      }
      case 'country': {
        const cntry = (u as Influencer).country || ch?.country || '';
        return (
          <td key={colId} className="text-center">
            {cntry ? (
              <Tooltip content={cntry.toUpperCase()}>
                <span className="inline-flex items-center justify-center gap-1">
                  <CountryFlag countryCode={cntry.toUpperCase()} size={18} />
                  <span className="country-flag-count leading-none">{cntry.toUpperCase()}</span>
                </span>
              </Tooltip>
            ) : ED}
          </td>
        );
      }
      case 'socials': {
        const inf = u as Influencer;
        const socialsObj = inf.socials || {};
        const mainChannelType = ch?.type;
        const mainChannelSubs = typeof inf.stats?.allTime?.subscribers === 'number' ? inf.stats.allTime.subscribers : null;
        const stats: { type: string; color: string; label: string; value: string | null; url?: string; emptyNoteEn?: string; emptyNoteKo?: string }[] = [];
        SOCIAL_PLATFORM_ENTRIES.forEach(({ type }) => {
          if (stats.some((entry) => entry.type === type)) return;
          if (type === 'discord') {
            const url = getChannelTypeSocialUrl(socialsObj, type);
            if (inf.discordMembers != null || inf.discordOnlineMembers != null) {
              stats.push({
                type,
                color: getChannelTypeColor(type),
                label: getChannelTypeMetricLabel(type) ?? 'Discord members (online/total)',
                value: `${fmtNum(inf.discordOnlineMembers ?? 0)}/${fmtNum(inf.discordMembers ?? 0)}`,
                url,
              });
            } else if (url) {
              stats.push({ type, color: getChannelTypeColor(type), label: getChannelTypeTitle(type), value: null, url });
            }
            return;
          }
          const followerField = getChannelTypeFollowerField(type);
          const url = getChannelTypeSocialUrl(socialsObj, type);
          if (followerField) {
            const rawValue = inf[followerField as keyof Influencer];
            if (typeof rawValue === 'number') {
              const isMainChannel = type === mainChannelType;
              // Every {platform}Followers field is confirmed to have no writer anywhere in ggc-be
              // v2 — BE confirmed Instagram's stopped for API quota reasons, and grepping ggc-be's
              // workers/services/api shows the same is true for every other platform's field too
              // (see docs/be-socials-followers-frozen-since-v1-20260902.md), so this isn't
              // Instagram-specific. When this platform is the tracked main channel, its own
              // channel-stat subscriber count is live and authoritative — show that instead of the
              // permanently-frozen socials value (no more reconciling the two against each other).
              //
              // Instagram specifically was called out by BE as "meaningless" data off the main
              // channel (quota-frozen, no substitute live number to fall back on either) — rather
              // than show a number known to be wrong, push an empty stat with a note explaining why
              // (instead of a numeric value) so it doesn't read as "no link" or "no data at all".
              const isStaleInstagramOffChannel = type === 'instagram' && !isMainChannel;
              if (isStaleInstagramOffChannel) {
                stats.push({
                  type,
                  color: getChannelTypeColor(type),
                  label: getChannelTypeMetricLabel(type) ?? `${getChannelTypeTitle(type)} followers`,
                  value: null,
                  url,
                  emptyNoteEn: 'Instagram follower collection was stopped due to API quota limits — showing live channel stats only when this is the tracked main channel.',
                  emptyNoteKo: 'API 쿼터 문제로 Instagram 팔로워 수집이 중단되어, 이 채널이 메인 채널로 추적 중일 때만 실시간 채널 통계를 표시합니다.',
                });
                return;
              }
              const displaySubs = (isMainChannel && mainChannelSubs !== null) ? mainChannelSubs : rawValue;
              stats.push({
                type,
                color: getChannelTypeColor(type),
                label: getChannelTypeMetricLabel(type) ?? `${getChannelTypeTitle(type)} followers`,
                value: fmtNum(displaySubs),
                url,
              });
              return;
            }
            // 플랫폼별 전용 필드가 없어도 메인 채널이면 stats.allTime.subscribers를 폴백으로 사용
            if (type === mainChannelType && mainChannelSubs !== null) {
              stats.push({
                type,
                color: getChannelTypeColor(type),
                label: getChannelTypeMetricLabel(type) ?? `${getChannelTypeTitle(type)} followers`,
                value: fmtNum(mainChannelSubs),
                url,
              });
              return;
            }
          }
          if (url) {
            stats.push({ type, color: getChannelTypeColor(type), label: getChannelTypeTitle(type), value: null, url });
          }
        });
        if (stats.length === 0) {
          return <td key={colId} className="min-w-[160px]">{showEmptyDash ? <EmptyDash /> : null}</td>;
        }
        return (
          <td key={colId} className="min-w-[160px]">
            <div className="socials-stats-cell">
              {stats.map((s) => {
                  const platformTitle = getChannelTypeTitle(s.type);
                  // Suppress ChannelTypeIcon's built-in tooltip — visible label already conveys platform name
                  const iconEl = (
                    <span className="socials-stats-icon-box">
                      <ChannelTypeIcon type={s.type} size={12} color={s.color} tooltip={null} />
                    </span>
                  );
                  const labelEl = (
                    <span className="socials-stats-label">{platformTitle}</span>
                  );
                  const linkEl = s.url ? (
                    <Tooltip content={t(`Open ${platformTitle} profile`, `${platformTitle} 프로필 열기`)}>
                      <a href={s.url} target="_blank" rel="noopener noreferrer" className="socials-stats-link">
                        {iconEl}
                        {labelEl}
                      </a>
                    </Tooltip>
                  ) : (
                    <Tooltip essential content={t(`No ${platformTitle} profile link configured for this influencer`, `이 인플루언서는 ${platformTitle} 프로필 링크가 등록되지 않았습니다`)}>
                      <span className="socials-stats-link socials-stats-link--no-url">
                        {iconEl}
                        {labelEl}
                      </span>
                    </Tooltip>
                  );
                  const valueRaw = s.value !== null
                    ? <span className="socials-stats-number">{s.value}</span>
                    : <span className="empty-dash"><EmptyDash /></span>;
                  // No more channel-vs-socials mismatch to reconcile here — the main-channel case
                  // already substitutes the live channel-stat number above, so there's only ever
                  // one value to show per platform now. `emptyNoteEn`/`emptyNoteKo` overrides the
                  // tooltip for a deliberately-empty value (e.g. stale off-channel Instagram) so it
                  // reads as "known unavailable, here's why" rather than a generic label.
                  const valueEl = (
                    <Tooltip essential content={s.emptyNoteEn ? t(s.emptyNoteEn, s.emptyNoteKo ?? s.emptyNoteEn) : s.label}>
                      {valueRaw}
                    </Tooltip>
                  );
                  return (
                    <div key={s.label} className="socials-stats-row">
                      {linkEl}
                      {valueEl}
                    </div>
                  );
                })}
            </div>
          </td>
        );
      }
      case 'email': {
        const inf = u as Influencer;
        return (
          <td key={colId} className="relative">
            <EmailCell inf={inf} selectedCampaignId={selectedCampaignId} emptyDash={ED} />
          </td>
        );
      }
      case 'estPrice': {
        const inf = u as Influencer;
        const campaignsById = new Map(campaigns.map((c) => [c._id, c]));
        const currentCampaign = selectedCampaignId ? campaignsById.get(selectedCampaignId) : null;
        return (
          <td key={colId} className="text-right whitespace-nowrap relative ggc-text-caption-m">
            <EstPriceCell user={inf} campaignsById={campaignsById} currentCampaign={currentCampaign} organizationId={organizationId} showEmptyDash={showEmptyDash} />
          </td>
        );
      }
      case 'recentGames': {
        const games = (u as Influencer).recentGames;
        const sortedGames = Array.isArray(games) ? [...games].filter(Boolean).sort((a, b) => percentOf(b) - percentOf(a)) : [];
        if (sortedGames.length === 0) return emptyTd(colId);
        const maxGamePct = Math.max(0, ...sortedGames.map(percentOf));
        return (
          <td key={colId} className="max-w-[210px]">
            <div className="label-list">{sortedGames.map((g, i) => {
              const title = titleOf(g);
              const pct = typeof g === 'object' && g?.percent != null ? g.percent : null;
              if (!title) return null;
              const barPct = pct != null && maxGamePct > 0 ? ((Number(pct) / maxGamePct) * 100).toFixed(1) : null;
              return <span key={`${title}_${i}`} className="ggc-tag ggc-tag--default" style={barPct != null ? { '--pct': `${barPct}%` } as React.CSSProperties : undefined}>{title}{pct != null ? ` (${pct}%)` : ''}</span>;
            })}</div>
          </td>
        );
      }
      case 'recentGenres': {
        const genres = (u as Influencer).recentGenres;
        const sortedGenres = Array.isArray(genres) ? [...genres].filter(Boolean).sort((a, b) => percentOf(b) - percentOf(a)) : [];
        if (sortedGenres.length === 0) return emptyTd(colId);
        const maxGenrePct = Math.max(0, ...sortedGenres.map(percentOf));
        return (
          <td key={colId} className="max-w-[210px]">
            <div className="label-list">{sortedGenres.map((g, i) => {
              const title = titleOf(g);
              const pct = typeof g === 'object' && g?.percent != null ? g.percent : null;
              if (!title) return null;
              const barPct = pct != null && maxGenrePct > 0 ? ((Number(pct) / maxGenrePct) * 100).toFixed(1) : null;
              return <span key={`${title}_${i}`} className="ggc-tag" style={barPct != null ? { '--pct': `${barPct}%` } as React.CSSProperties : undefined}>{title}{pct != null ? ` (${pct}%)` : ''}</span>;
            })}</div>
          </td>
        );
      }
      case 'rav5': {
        const inf = u as Influencer;
        return (
          <td key={colId} className="ggc-text-caption-m">
            <GeneralViewershipCell user={inf} abbrev={shortSubsFormat} showEmptyDash={showEmptyDash} />
            {/* Freshness of the viewership stats = channel stats crawl (statsUpdatedAt). Admin-only,
                and toggled by the "Show Updated At on stats columns" table view option. */}
            {showColumnUpdatedAt && <ColumnUpdatedAt iso={inf.statsUpdatedAt} />}
          </td>
        );
      }
      case 'eng5': {
        const inf = u as Influencer;
        return (
          <td key={colId} className="ggc-text-caption-m">
            <EngagementCell user={inf} showEmptyDash={showEmptyDash} />
          </td>
        );
      }
      case 'kpi': {
        const inf = u as Influencer;
        const campaignKpi = (selectedCampaignId && selectedCampaignId !== 'all')
          ? inf.kpisByCampaign?.find((c) => c.campaignId === selectedCampaignId) as Record<string, unknown> | undefined
          : undefined;
        return (
          <td key={colId} className="ggc-text-caption-s">
            <DetailedKPICell user={inf} forceKpi={kpiOption ?? 'freeChoice'} campaignKpi={campaignKpi} showEmptyDash={showEmptyDash} />
          </td>
        );
      }
      case 'dem': {
        const inf = u as Influencer;
        const demo = inf.demographic;
        // Demographic percentages are stored 0–100. Some legacy records hold
        // out-of-range values (e.g. percent stored ×100). Clamp the bar widths
        // so an anomalous value can't overflow the cell and bleed across the
        // row; the numeric labels still show the raw value to flag bad data.
        const clampBarPct = (n: number) => (Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : 0);
        const genderM = demo?.gender?.male;
        const genderF = demo?.gender?.female;
        const genderMPct = genderM != null ? Number(genderM) : null;
        const genderFPct = genderF != null ? Number(genderF) : null;
        const ageGroup = demo?.ageGroup as Record<string, number> | undefined;
        const deviceType = demo?.deviceType as Record<string, number> | undefined;
        const geo = demo?.geographic;
        const ageGroups = ['13-17', '18-24', '25-34', '35-44', '45-54', '55-64'];
        const devTypes = ['DESKTOP', 'GAME_CONSOLE', 'MOBILE', 'TABLET', 'TV', 'UNKNOWN_PLATFORM'];
        const devIcons: Record<string, string> = { DESKTOP: 'desktop', GAME_CONSOLE: 'gamepad', MOBILE: 'mobile-phone', TABLET: 'tablet', TV: 'tv', UNKNOWN_PLATFORM: 'question-circle' };
        const geoEntries = geo ? Object.entries(geo).slice(0, 5) : [];
        const screenshotCount = Array.isArray(demo?.screenshots) ? demo.screenshots.length : 0;
        const hasScreenshots = screenshotCount > 0;
        const screenshotBadge = hasScreenshots ? (
          <Tooltip content={t(
            `Demographic screenshot${screenshotCount > 1 ? 's' : ''} uploaded (${screenshotCount})`,
            `데모그라픽 스크린샷이 업로드되어 있습니다 (${screenshotCount}건)`,
          )}>
            <span className="dem-screenshot-badge" aria-label={t('Has demographic screenshot', '데모그라픽 스크린샷 있음')}>
              <i className="fa fa-camera" />
            </span>
          </Tooltip>
        ) : null;
        const hasAnyData = (genderM != null || genderF != null)
          || (ageGroup && (ageGroups.some((ag) => ageGroup[`age${ag}`] != null) || ageGroup.others != null))
          || (deviceType && Object.keys(deviceType).length > 0)
          || geoEntries.length > 0;
        const infoBtn = (
          <RowActionButton className="mt-1" asChild>
            <a
              href={`/panel/user/${inf._id}/overview`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                if (onOpenPanelDrawer) {
                  e.preventDefault();
                  onOpenPanelDrawer(inf._id);
                }
              }}
            >
              {t('Edit', '편집')}
            </a>
          </RowActionButton>
        );
        if (!hasAnyData) {
          return (
            <td key={colId} style={{ fontSize: 10, padding: '4px 6px', maxWidth: 180, verticalAlign: 'middle', textAlign: 'center', position: 'relative' }}>
              {screenshotBadge}
              <div className="dem-empty-cell flex flex-col items-center gap-1">
                {showEmptyDash && <EmptyDash />}
                {infoBtn}
              </div>
            </td>
          );
        }
        return (
          <td key={colId} className="dem-cell">
            {screenshotBadge}
            {(genderM != null || genderF != null) && (
              <div className="dem-section dem-section--gender">
                <span className="dem-section-label">{t('Gender', '성별')}</span>
                <div className="dem-section-body">
                  <div className="dem-row">
                    {genderMPct != null && Number.isFinite(genderMPct) && (
                      <Tooltip content="Male">
                        <span className="dem-labeled dem-labeled--no-bar">
                          <i className="fa fa-mars" style={{ color: 'var(--ggc-blue)' }} /> {genderMPct.toFixed(1)}%
                        </span>
                      </Tooltip>
                    )}
                    {genderFPct != null && Number.isFinite(genderFPct) && (
                      <Tooltip content="Female">
                        <span className="dem-labeled dem-labeled--no-bar">
                          <i className="fa fa-venus" style={{ color: 'var(--ggc-sub-pink)' }} /> {genderFPct.toFixed(1)}%
                        </span>
                      </Tooltip>
                    )}
                  </div>
                  <div
                    className="dem-gender-bar"
                    style={{
                      '--male-half': `${clampBarPct(genderMPct != null && Number.isFinite(genderMPct) ? genderMPct : 0) / 2}%`,
                      '--female-half': `${clampBarPct(genderFPct != null && Number.isFinite(genderFPct) ? genderFPct : 0) / 2}%`,
                    } as React.CSSProperties}
                  />
                </div>
              </div>
            )}
            {ageGroup && (ageGroups.some((ag) => ageGroup[`age${ag}`] != null) || ageGroup.others != null) && (
              <div className="dem-section">
                <span className="dem-section-label">{t('Age', '연령대')}</span>
                <div className="dem-age-rows">
                  {ageGroups.map((ag) => {
                    const key = `age${ag}`;
                    const val = ageGroup[key];
                    if (val == null) return null;
                    const pct = Number(val).toFixed(1);
                    return (
                      <div key={ag} className="dem-age-row" style={{ '--pct': `${clampBarPct(Number(val))}%` } as React.CSSProperties}>
                        <span className="dem-age-label">{ag}</span>
                        <span className="dem-age-pct">{pct}%</span>
                      </div>
                    );
                  })}
                  {ageGroup.others != null && (
                    <div className="dem-age-row" style={{ '--pct': `${clampBarPct(Number(ageGroup.others))}%` } as React.CSSProperties}>
                      <span className="dem-age-label">{t('Others', '기타')}</span>
                      <span className="dem-age-pct">{Number(ageGroup.others).toFixed(1)}%</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            {deviceType && Object.keys(deviceType).length > 0 && (
              <div className="dem-section">
                <span className="dem-section-label">{t('Device', '디바이스')}</span>
                <div className="dem-section-body">
                  <div className="dem-row">
                    {devTypes.map((type) => {
                      const val = deviceType[type];
                      if (val == null) return null;
                      const valN = Number(val);
                      if (!Number.isFinite(valN)) return null;
                      const total = devTypes.reduce((s, tp) => s + (Number(deviceType[tp]) || 0), 0);
                      const pct = total > 0 ? (valN * 100) / total : 0;
                      const icon = devIcons[type] ?? 'question-circle';
                      const devLabel: Record<string, string> = { DESKTOP: 'Desktop', GAME_CONSOLE: 'Game Console', MOBILE: 'Mobile', TABLET: 'Tablet', TV: 'TV', UNKNOWN_PLATFORM: 'Unknown' };
                      return <Tooltip key={type} content={devLabel[type] ?? type}><span className="dem-labeled" style={{ '--pct': `${pct.toFixed(1)}%` } as React.CSSProperties}><i className={`fa fa-${icon} mr-1`} /> {pct.toFixed(1)}%</span></Tooltip>;
                    })}
                  </div>
                </div>
              </div>
            )}
            {geoEntries.length > 0 && (
              <div className="dem-section">
                <span className="dem-section-label">{t('Country', '국가')}</span>
                <div className="dem-section-body">
                  <div className="dem-row">
                    {geoEntries.map(([code, val]) => {
                      const geoPct = val != null ? Number(val) : NaN;
                      const geoPctStr = Number.isFinite(geoPct) ? geoPct.toFixed(1) : '0';
                      return (
                        <span key={code} className="dem-labeled dem-country" style={{ '--pct': `${clampBarPct(geoPct)}%` } as React.CSSProperties}>
                          <span className="dem-country-code">{code === 'others' ? '...' : code.toUpperCase()}</span> {geoPctStr}%
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            {infoBtn}
          </td>
        );
      }
      case 'supclickDem': {
        const inf = u as Influencer;
        const dc = inf.profileDemographicV2?.allTime ?? inf.profileDemographic?.allTime;
        if (!dc) return emptyTd(colId);
        const SUPCLICK_COUNTRY_FIX: Record<string, string> = { UK: 'GB', EN: 'GB', JP: 'JP', KO: 'KR', JA: 'JP', EL: 'GR' };
        const fixSupclickCountryCode = (code: string) => SUPCLICK_COUNTRY_FIX[code.toUpperCase()] ?? code.toUpperCase();
        const toRows = (raw: Record<string, { count?: number; label?: string; id?: string }> | undefined): { label: string; percent: string }[] => {
          if (!raw) return [];
          const sorted = Object.values(raw).sort((a, b) => (b.count || 0) - (a.count || 0));
          const top = sorted.slice(0, 5);
          const sumCount = sorted.reduce((s, r) => s + (r.count || 0), 0);
          if (sorted.length > 5) {
            const otherCount = sorted.slice(5).reduce((s, r) => s + (r.count || 0), 0);
            top.push({ count: otherCount, label: 'Other' });
          }
          return top.map((row) => {
            const pct = sumCount > 0 ? ((row.count || 0) * 100) / sumCount : 0;
            return { label: row.label || (row as { id?: string }).id || '?', percent: pct.toFixed(1) + '%' };
          });
        };
        type CountryRow = { count?: number; label?: string; id?: string; countryCode?: string; code?: string; country?: string };
        const toCountryRows = (raw: Record<string, CountryRow> | CountryRow[] | undefined): { label: string; percent: string; code: string }[] => {
          if (!raw) return [];
          const entries: [string, CountryRow][] = Array.isArray(raw)
            ? raw.map((row, i) => [(row.id ?? row.countryCode ?? row.code ?? row.country) ?? String(i), row])
            : Object.entries(raw);
          const sorted = entries.sort(([, a], [, b]) => ((typeof b === 'object' && b?.count) || (typeof b === 'number' ? b : 0)) - ((typeof a === 'object' && a?.count) || (typeof a === 'number' ? a : 0)));
          const top = sorted.slice(0, 5);
          const getCount = (r: CountryRow | number) => typeof r === 'number' ? r : (r?.count ?? 0);
          const sumCount = sorted.reduce((s, [, r]) => s + getCount(r), 0);
          const extractCode = (row: CountryRow, label: string, key: string) => {
            const candidates = [row.id, row.countryCode, row.code, row.country, label, key];
            return candidates.find((c) => c && /^[A-Za-z]{2}$/.test(c)) ?? '';
          };
          const result = top.map(([key, row]) => {
            const cnt = getCount(row);
            const pct = sumCount > 0 ? (cnt * 100) / sumCount : 0;
            const label = (typeof row === 'object' && row?.label) || (typeof row === 'object' && row?.id) || (typeof row === 'object' && row?.countryCode) || (typeof row === 'object' && row?.code) || key;
            const code = extractCode(typeof row === 'object' ? row : {}, label, key);
            return { label, percent: pct.toFixed(1) + '%', code };
          });
          if (sorted.length > 5) {
            const otherCount = sorted.slice(5).reduce((s, [, r]) => s + (r.count || 0), 0);
            const pct = sumCount > 0 ? (otherCount * 100) / sumCount : 0;
            result.push({ label: 'Other', percent: pct.toFixed(1) + '%', code: '' });
          }
          return result;
        };
        const countryRows = toCountryRows(dc.countries as Record<string, { count?: number; label?: string; id?: string }>);
        const deviceRows = toRows(dc.deviceTypes as Record<string, { count?: number; label?: string; id?: string }>);
        let totalClicks = dc.shortUrlClicks || dc.longUrlClicks || 0;
        if (!totalClicks && dc.countries) {
          totalClicks = Object.values(dc.countries).reduce((s, r) => s + ((r as { count?: number }).count || 0), 0);
        }
        const hasContent = countryRows.length > 0 || deviceRows.length > 0;
        return (
          <td key={colId} className="supclick-dem-cell">
            {countryRows.length > 0 && (
              <div className="supclick-section supclick-section--country">
                <div className="supclick-section-title">{t('Countries', '국가')}</div>
                <div className="supclick-rows">
                  {countryRows.map((r) => {
                    const flagCode = r.code || (/^[A-Za-z]{2}$/.test(r.label) ? r.label : '');
                    const isOtherOrUnknown = (l: string) => {
                      const x = l.toLowerCase().trim();
                      return x === 'other' || /^\*+$/.test(l) || x === 'unknown' || x === 'n/a' || x === '?' || x === '-' || x === '??';
                    };
                    const showGlobe = !flagCode || isOtherOrUnknown(r.label);
                    return (
                      <div key={`${r.label}-${r.percent}`} className="supclick-row" style={{ '--pct': r.percent } as React.CSSProperties}>
                        <span title={showGlobe ? undefined : fixSupclickCountryCode(flagCode)}>
                          <CountryFlag countryCode={showGlobe ? undefined : fixSupclickCountryCode(flagCode)} size={14} />
                        </span>
                        <span className="supclick-label">{r.label}</span>
                        <span className="supclick-pct">{r.percent}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {deviceRows.length > 0 && (
              <div className="supclick-section supclick-section--device">
                <div className="supclick-section-title">{t('Device types', '디바이스')}</div>
                <div className="supclick-rows">
                  {deviceRows.map((r) => {
                    const deviceIcon = (() => {
                      const l = r.label.toLowerCase().trim();
                      if (l.includes('desktop') || l === 'pc') return 'fa-desktop';
                      if (l.includes('mobile') || l.includes('phone') || l.includes('smartphone')) return 'fa-mobile';
                      if (l.includes('tablet')) return 'fa-tablet';
                      return 'fa-globe';
                    })();
                    return (
                      <div key={r.label} className="supclick-row" style={{ '--pct': r.percent } as React.CSSProperties}>
                        <span className="supclick-flag-wrap" title={r.label}>
                          <i className={`fa ${deviceIcon} text-xs text-ggc-text-gray`} aria-hidden />
                        </span>
                        <span className="supclick-label">{r.label}</span>
                        <span className="supclick-pct">{r.percent}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {!hasContent && ED}
            {totalClicks > 0 && (
              <div className="supclick-basedon">Based on {totalClicks.toLocaleString()} clicks</div>
            )}
          </td>
        );
      }
      case 'lva': {
        const inf = u as Influencer;
        return (
          <td key={colId} className="whitespace-nowrap ggc-text-caption-m">
            <LastVideoCell user={inf} showEmptyDash={showEmptyDash} />
            {/* Freshness of the latest-post data = channel stats crawl (statsUpdatedAt). Admin-only,
                and toggled by the "Show Updated At on stats columns" table view option. */}
            {showColumnUpdatedAt && <ColumnUpdatedAt iso={inf.statsUpdatedAt} />}
          </td>
        );
      }
      case 'statsUpdatedAt': {
        // Not server-sortable (BE has no sort field for this yet) — see SORTABLE_COLUMN_IDS in index.tsx
        const statsUpdatedAt = (u as Influencer).statsUpdatedAt;
        if (!statsUpdatedAt) return emptyTd(colId);
        return (
          <td key={colId} className="whitespace-nowrap ggc-text-caption-m">
            <div className="stats-updated-at-cell">
              {/* CHANNEL stats crawl (subscribers + last-5 views/engagement + latest post),
                  not the social-followers-only refresh. Bold label, then date-time, then a
                  pill with the relative age. */}
              <span className="stats-updated-at__label">{t('Channel stats', '채널 통계')}</span>
              <span>{formatLocalDateTime(statsUpdatedAt)}</span>
              <span className="stats-updated-at__ago">{relativeDate(statsUpdatedAt)}</span>
            </div>
          </td>
        );
      }
      case 'videos': {
        const inf = u as Influencer;
        const userId = inf._id ?? inf.id ?? '';
        // perUserAnalysis holds enriched post data (title, url, stats) from the API
        // userCampaign.posts only has post IDs — use perUserAnalysis as primary source
        const analysisPostsForUser = perUserAnalysis?.[userId]?.posts ?? [];
        const userCampaign = inf.campaigns?.find((c) => c.campaignId === selectedCampaignId);
        const rawPosts = analysisPostsForUser.length > 0
          ? analysisPostsForUser
          : (userCampaign?.posts ?? []);
        if (rawPosts.length === 0) return emptyTd(colId);
        const sortedPosts = [...rawPosts].sort((a, b) => {
          const da = a.campaignGameStreamedAt ?? a.publishedAt ?? '';
          const db = b.campaignGameStreamedAt ?? b.publishedAt ?? '';
          return db.localeCompare(da);
        });
        return (
          <td key={colId} className="ggc-text-caption-m" style={{ minWidth: 300, maxWidth: 380 }}>
            <VideoPostsList posts={sortedPosts} />
          </td>
        );
      }
      case 'categories': {
        const cats = (u as Influencer).categories;
        const catList = Array.isArray(cats) ? cats : [];
        if (catList.length === 0) return emptyTd(colId);
        return (
          <td key={colId} className="max-w-[340px] min-w-[220px]">
            <CollapsibleTagList
              items={catList}
              renderItem={(c, i) => <span key={`${c}-${i}`} className="ggc-tag ggc-tag--default">{c}</span>}
            />
          </td>
        );
      }
      case 'incComment': {
        const inf = u as Influencer;
        const tokenData: Record<string, { names: string[]; isSuggested?: boolean; order: number }> = {};
        const isCampaignSpecific = !!(selectedCampaignId && selectedCampaignId !== 'all');
        const uc = inf.campaigns?.find((c) => c.campaignId === selectedCampaignId);
        const parseTokens = (s?: string) => (s ?? '').split(',').map((x) => x.trim()).filter(Boolean);

        // Delete targets for the admin editor: each place a token currently lives.
        // Entries without a campaignId have no writable target — their tokens are
        // tracked separately so the cell can still show them (disabled).
        const targets: CommentTargetData[] = [];
        const unresolvableTokens: string[] = [];

        if (isCampaignSpecific) {
          const raw = parseTokens(uc?.comment);
          const camp = campaigns.find((c) => (c._id ?? c.id) === selectedCampaignId);
          for (const tk of raw) tokenData[tk] = { names: camp ? [camp.name ?? ''] : ['Private Draft'], order: 1 };
          // Always register the campaign as a target (even with no tokens yet) so a
          // comment added via the editor shows up without a refetch.
          targets.push({ campaignId: selectedCampaignId!, label: camp?.name || 'Private Draft', isDefault: false, tokens: raw });
        } else {
          const tokenCampaigns: Record<string, Campaign[]> = {};
          for (const ucItem of inf.campaigns ?? []) {
            if (organizationId && ucItem.organizationId && ucItem.organizationId !== organizationId) continue;
            if (!ucItem.comment) continue;
            const raw = parseTokens(ucItem.comment);
            const camp = ucItem.campaignId ? campaigns.find((c) => (c._id ?? c.id) === ucItem.campaignId) : undefined;
            for (const tk of raw) {
              if (!tokenCampaigns[tk]) tokenCampaigns[tk] = [];
              tokenCampaigns[tk].push(camp as Campaign);
            }
            if (ucItem.campaignId) targets.push({ campaignId: ucItem.campaignId, label: camp?.name || 'Private Draft', isDefault: false, tokens: raw });
            else unresolvableTokens.push(...raw);
          }
          for (const [tk, camps] of Object.entries(tokenCampaigns)) {
            const names = camps
              .sort((a, b) => ((b?.updatedAt ?? '') as string).localeCompare((a?.updatedAt ?? '') as string))
              .map((c) => c?.name || 'Private Draft')
              .filter((n, i, arr) => arr.indexOf(n) === i);
            tokenData[tk] = { names, order: 1 };
          }
          // The default/suggested comment lives in `suggestedComment` (BE + gc-mono);
          // the FE's `inclusionComment` is never populated by the list endpoint. Fall
          // back defensively for any endpoint that might still send the old name. Gated
          // by `canSeeSuggested` so it isn't exposed to users without the permission.
          if (canSeeSuggested) {
            const defaultRaw = parseTokens(inf.suggestedComment ?? inf.inclusionComment);
            for (const tk of defaultRaw) {
              if (tokenData[tk]) {
                tokenData[tk].order = 0;
                tokenData[tk].isSuggested = true;
              } else {
                tokenData[tk] = { names: [], order: 0, isSuggested: true };
              }
            }
            // Always register the default comment as a target (even when empty) so a
            // token added to it via the editor renders without a refetch.
            targets.push({ campaignId: 'default', label: '', isDefault: true, tokens: defaultRaw });
          }
        }

        const tokens = Object.keys(tokenData).sort((a, b) => {
          const da = tokenData[a], db = tokenData[b];
          if (da.order !== db.order) return da.order - db.order;
          if (da.names.length !== db.names.length) return db.names.length - da.names.length;
          return a.localeCompare(b);
        });

        if (isAdmin) {
          const editCampaignId = isCampaignSpecific ? (selectedCampaignId ?? '') : 'default';
          return (
            <td key={colId}>
              <IncCommentCellAdmin
                inf={inf}
                userId={inf._id}
                targets={targets}
                unresolvableTokens={unresolvableTokens}
                editCampaignId={editCampaignId}
              />
            </td>
          );
        }

        if (tokens.length === 0) return emptyTd(colId);
        return (
          <td key={colId}>
            <IncCommentCell tokens={tokens} tokenCampaignMap={tokenData} />
          </td>
        );
      }
      case 'status': {
        const uc = (u as Influencer).campaigns?.find((c) => c.campaignId === selectedCampaignId);
        const st = uc?.status ?? '';
        if (!st) return emptyTd(colId);
        const meta = CAMPAIGN_INFLUENCER_STATUSES.find((s) => s.value === st);
        // Influencer status names are shown in English only, regardless of app language.
        const label = meta ? meta.text : formatLabelText(st);
        // Tone comes from the shared status constant so this badge, the influencer
        // profile's status pill and payment review all color a status identically.
        const badgeStatus = getInfluencerStatusBadge(st);
        return (
          <td key={colId} className="whitespace-nowrap">
            <GgcBadge status={badgeStatus}>{label}</GgcBadge>
          </td>
        );
      }
      case 'campaignStats': {
        const userId = u._id ?? (u as Influencer).id ?? '';
        const posts = perUserAnalysis?.[userId]?.posts ?? [];
        const views = posts.reduce((s, p) => s + (p.stats?.views ?? 0), 0);
        const likes = posts.reduce((s, p) => s + (p.stats?.likes ?? 0), 0);
        const comments = posts.reduce((s, p) => s + (p.stats?.comments ?? 0), 0);
        const hasAny = views > 0 || likes > 0 || comments > 0;
        if (!hasAny && !showEmptyMetricIcons) return emptyTd(colId);
        // Best-effort: uses the row's primary channel type to pick platform-accurate
        // icons, even though a campaign's posts can in principle span multiple channels.
        const statsChType = ch?.type ?? 'youtube';
        const metrics: { key: string; icon: string; label: string; value: number }[] = [
          { key: 'views',    icon: getChannelTypeMetricIcon(statsChType, 'views'),    label: t('Views', '조회수'),     value: views },
          { key: 'likes',    icon: getChannelTypeMetricIcon(statsChType, 'likes'),    label: t('Likes', '좋아요'),   value: likes },
          { key: 'comments', icon: getChannelTypeMetricIcon(statsChType, 'comments'), label: t('Comments', '코멘트'), value: comments },
        ];
        return (
          <td key={colId} className="whitespace-nowrap ggc-text-caption-m">
            <div className="flex flex-col gap-1.5">
              {metrics.map((m) => (
                <div key={m.key} className="flex items-center gap-1">
                  <Tooltip content={m.label}>
                    <i className={`fa fa-${m.icon} ${m.value > 0 ? 'text-ggc-text-gray' : 'text-ggc-border-gray-dark'}`} style={{ minWidth: 14 }} />
                  </Tooltip>
                  {m.value > 0 ? fmtNum(m.value) : null}
                </div>
              ))}
            </div>
          </td>
        );
      }
      case 'campaignClicks': {
        const userId = u._id ?? (u as Influencer).id ?? '';
        const posts = perUserAnalysis?.[userId]?.posts ?? [];
        const sums: Record<string, number> = {};
        posts.forEach((p) => p.links?.forEach((l) => {
          const k = l.linkType ?? 'other';
          sums[k] = (sums[k] ?? 0) + (l.stats?.clicks ?? 0);
        }));
        const web = (sums.web ?? 0) + (sums.gameWebURL ?? 0);
        const ios = (sums.ios ?? 0) + (sums.gameiOSURL ?? 0);
        const android = (sums.android ?? 0) + (sums.gameAndroidURL ?? 0);
        const other = sums.defaultURL ?? 0;
        const hasAny = web > 0 || ios > 0 || android > 0 || other > 0;
        if (!hasAny && !showEmptyMetricIcons) return emptyTd(colId);
        const items: { icon: string; label: string; value: number }[] = [
          { icon: 'globe',           label: t('Web', '웹'),       value: web },
          { icon: 'apple',           label: t('iOS', 'iOS'),       value: ios },
          { icon: 'android',         label: t('Android', '안드로이드'), value: android },
          { icon: 'hand-pointer-o',  label: t('Other link', '기타 링크'), value: other },
        ];
        return (
          <td key={colId} className="whitespace-nowrap ggc-text-caption-m">
            <div className="flex flex-col gap-1.5">
              {items.map((m) => (
                <div key={m.icon} className="flex items-center gap-1">
                  <Tooltip content={m.label}>
                    <i className={`fa fa-${m.icon} ${m.value > 0 ? 'text-ggc-text-gray' : 'text-ggc-border-gray-dark'}`} style={{ minWidth: 14 }} />
                  </Tooltip>
                  {m.value > 0 ? fmtNum(m.value) : null}
                </div>
              ))}
            </div>
          </td>
        );
      }
      case 'clicksDemographic': {
        const userId = u._id ?? (u as Influencer).id ?? '';
        const posts = perUserAnalysis?.[userId]?.posts ?? [];
        if (posts.length === 0) return emptyTd(colId);
        const countryMap: Record<string, number> = {};
        posts.forEach((p) => p.links?.forEach((l) => {
          const allTime = l.stats?.data?.allTime as Record<string, unknown> | undefined;
          const countries = allTime?.countries as { id?: string; count?: number }[] | undefined;
          if (Array.isArray(countries)) {
            countries.forEach(({ id, count }) => {
              if (id) countryMap[id] = (countryMap[id] ?? 0) + (count ?? 0);
            });
          }
        }));
        const totalClicks = Object.values(countryMap).reduce((a, b) => a + b, 0);
        if (totalClicks === 0) return emptyTd(colId);
        const CLICK_DEM_COUNTRY_FIX: Record<string, string> = { UK: 'GB', EN: 'GB', JP: 'JP', KO: 'KR', JA: 'JP', EL: 'GR' };
        const fixCode = (code: string) => CLICK_DEM_COUNTRY_FIX[code.toUpperCase()] ?? code.toUpperCase();
        const isOtherCode = (c: string) => !c || c.length !== 2 || c === '??';
        const top5 = Object.entries(countryMap)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 5)
          .map(([id, cnt]) => ({ id, pct: ((cnt / totalClicks) * 100).toFixed(1) }));
        return (
          <td key={colId} className="supclick-dem-cell">
            <div className="supclick-section supclick-section--country">
              <div className="supclick-section-title">{t('Countries', '국가')}</div>
              <div className="supclick-rows">
                {top5.map(({ id, pct }) => {
                  const code = fixCode(id);
                  const showGlobe = isOtherCode(code);
                  return (
                    <div key={id} className="supclick-row" style={{ '--pct': `${pct}%` } as React.CSSProperties}>
                      <span title={showGlobe ? undefined : code}>
                        <CountryFlag countryCode={showGlobe ? undefined : code} size={14} />
                      </span>
                      <span className="supclick-label">{id.toUpperCase()}</span>
                      <span className="supclick-pct">{pct}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="supclick-basedon">Based on {totalClicks.toLocaleString()} clicks</div>
          </td>
        );
      }
      case 'lastMessageAt': {
        const uc = (u as Influencer).campaigns?.find((c) => c.campaignId === selectedCampaignId);
        const sentAt = uc?.lastMessage?.sentAt;
        return (
          <td key={colId} className="whitespace-nowrap ggc-text-caption-m">
            {sentAt ? sentAt.replace('T', ' ').slice(0, 16) : ED}
          </td>
        );
      }
      case 'lastMessageSubject': {
        const uc = (u as Influencer).campaigns?.find((c) => c.campaignId === selectedCampaignId);
        const subject = uc?.lastMessage?.subject;
        const quote = uc?.lastMessage?.quote;
        if (!subject && !quote) return emptyTd(colId);
        return (
          <td key={colId} className="ggc-text-caption-m" style={{ maxWidth: 160 }}>
            {subject && <Tooltip content={subject}><b className="block truncate">{subject.slice(0, 15)}{subject.length > 15 ? '…' : ''}</b></Tooltip>}
            {quote && <Tooltip content={quote}><em className="block truncate text-ggc-text-gray">{quote.slice(0, 35)}{quote.length > 35 ? '…' : ''}</em></Tooltip>}
          </td>
        );
      }
      case 'proposalPrice': {
        const uc = (u as Influencer).campaigns?.find((c) => c.campaignId === selectedCampaignId);
        const price = uc?.priceProposal;
        // Initial offer was editable inline in the original (no deleted guard).
        const disabledReason = campaignEditDisabledReason(t, selectedCampaignId, uc, { allowDeleted: true });
        return (
          <td key={colId} className="text-center whitespace-nowrap ggc-text-caption-m tabular-nums">
            {isAdmin && !isSibling ? (
              <InlinePriceEditor
                type="proposal"
                userId={u._id}
                campaignId={selectedCampaignId ?? ''}
                value={price}
                disabledReason={disabledReason}
                onSaved={(v) => { if (uc) uc.priceProposal = v; }}
              />
            ) : (
              price != null ? <span>${price.toLocaleString()}</span> : ED
            )}
          </td>
        );
      }
      case 'finalPrice': {
        const uc = (u as Influencer).campaigns?.find((c) => c.campaignId === selectedCampaignId);
        const camp = campaigns.find((c) => (c._id ?? c.id) === selectedCampaignId);
        const price = uc?.finalPrice ?? uc?.priceFinal;
        // Price set via work order is read-only — surface that as a disabled
        // editor with a tooltip instead of hiding the button.
        const workOrderPrice = (uc as Record<string, unknown>)?.priceWorkOrder as number | undefined;
        const displayPrice = workOrderPrice ?? price;
        let disabledReason = campaignEditDisabledReason(t, selectedCampaignId, uc);
        if (!disabledReason && workOrderPrice) {
          disabledReason = t('Price is set by a work order and cannot be edited directly', '워크오더로 설정된 가격이라 직접 편집할 수 없습니다');
        }
        const labelOf = (p?: string) => (p ? advertisingTypesMap[p] ?? p : '');
        return (
          <td key={colId} className="text-right whitespace-nowrap">
            <div className="flex flex-col items-end gap-0.5">
              <span className="ggc-text-caption-m tabular-nums">
                {displayPrice != null ? `$${displayPrice.toLocaleString()}` : <span className="text-ggc-disabled">—</span>}
              </span>
              {renderBreakdownChips(uc?.priceBreakDown, 'final', labelOf)}
              {isAdmin && !isSibling && (
                <FinalPriceEditor
                  userId={u._id}
                  campaignId={selectedCampaignId ?? ''}
                  disabledReason={disabledReason}
                  priceBreakDown={uc?.priceBreakDown}
                  commissionRate={camp?.commissionRate}
                  currentVideoType={uc?.candidateManualEstimation?.partnership}
                  existingFinalPrice={uc?.priceFinal ?? uc?.finalPrice}
                  existingAdvertiserPrice={uc?.priceAdvertiser ?? uc?.advertiserPrice}
                  onSaved={(finalTotal, advertiserTotal, breakdown) => {
                    if (uc) {
                      uc.priceFinal = finalTotal ?? undefined;
                      uc.finalPrice = finalTotal ?? undefined;
                      uc.priceAdvertiser = advertiserTotal ?? undefined;
                      uc.priceBreakDown = breakdown;
                    }
                    forceRowUpdate();
                  }}
                />
              )}
            </div>
          </td>
        );
      }
      case 'advertiserPrice': {
        const uc = (u as Influencer).campaigns?.find((c) => c.campaignId === selectedCampaignId);
        const camp = campaigns.find((c) => (c._id ?? c.id) === selectedCampaignId);
        const storedPrice = uc?.priceAdvertiser ?? uc?.advertiserPrice;
        // Advertiser price defaults to final price × commission rate when unset.
        const finalForCalc = uc?.priceFinal ?? uc?.finalPrice ?? 0;
        const rate = camp?.commissionRate ?? 0;
        const computed = ceilWithCommission(finalForCalc, rate);
        const fallback = computed > 0 ? computed : undefined;
        const displayPrice = storedPrice ?? fallback;
        const labelOf = (p?: string) => (p ? advertisingTypesMap[p] ?? p : '');
        const disabledReason = campaignEditDisabledReason(t, selectedCampaignId, uc);
        return (
          <td key={colId} className="whitespace-nowrap">
            <div className="flex flex-col items-center gap-0.5">
              {isAdmin && !isSibling ? (
                <InlinePriceEditor
                  type="advertiser"
                  userId={u._id}
                  campaignId={selectedCampaignId ?? ''}
                  value={storedPrice}
                  fallback={fallback}
                  disabledReason={disabledReason}
                  onSaved={(v) => { if (uc) { uc.priceAdvertiser = v; uc.advertiserPrice = v; } }}
                />
              ) : (
                <span className="ggc-text-caption-m tabular-nums">{displayPrice != null ? `$${displayPrice.toLocaleString()}` : <span className="text-ggc-disabled">—</span>}</span>
              )}
              {isAdmin && !isSibling && rate > 0 && finalForCalc > 0 && (
                <Tooltip
                  essential
                  content={(
                    <div className="flex flex-col gap-0.5 text-left whitespace-nowrap">
                      <span>Final offer: ${finalForCalc.toLocaleString()}</span>
                      <span>Commission rate: ×{rate.toFixed(2)} ({Math.round((rate - 1) * 100)}%)</span>
                      <span>Advertiser price: ${computed.toLocaleString()}</span>
                      <span className="mt-0.5 border-t border-white/20 pt-0.5 opacity-70">
                        {t('Not visible to advertisers', '광고주에게는 보이지 않습니다')}
                      </span>
                    </div>
                  )}
                >
                  <span className="ggc-text-caption-s text-ggc-text-gray tabular-nums cursor-help">
                    ${finalForCalc.toLocaleString()} × {rate.toFixed(2)} = ${computed.toLocaleString()}
                  </span>
                </Tooltip>
              )}
              {renderBreakdownChips(uc?.priceBreakDown, 'advertiser', labelOf)}
            </div>
          </td>
        );
      }
      case 'videoType': {
        const uc = (u as Influencer).campaigns?.find((c) => c.campaignId === selectedCampaignId);
        // `workOrders` is injected on the top-level user (not per-campaign) by BE
        // `_injectUserWorkOrders` — scope it to the viewed campaign here.
        const workOrderItems = (u as Influencer).workOrders
          ?.filter((w) => w.campaignId === selectedCampaignId)
          .flatMap((w) => w.items ?? []) ?? [];
        const workOrderTags: { type: string; count: number }[] = [];
        for (const item of workOrderItems) {
          if (!item.advertisingType) continue;
          const existing = workOrderTags.find((w) => w.type === item.advertisingType);
          if (existing) existing.count += item.count ?? 0;
          else workOrderTags.push({ type: item.advertisingType, count: item.count ?? 0 });
        }
        // Work-order video types are read-only (set on the work order) — same as original.
        // Shown as a distinct badge (vs. the plain tag below) so an actually-issued
        // work order is visually distinguishable from the negotiated/planned video type.
        if (workOrderTags.length > 0) {
          return (
            <td key={colId} className="ggc-text-caption-m">
              {workOrderTags.map((w) => (
                <Tooltip essential key={w.type} content={t('Work order issued', '워크오더 발행됨')}>
                  <GgcBadge status="muted" hideDot className="mr-1">
                    {advertisingTypesMap[w.type] ?? w.type} ({w.count})
                  </GgcBadge>
                </Tooltip>
              ))}
            </td>
          );
        }
        const partnership = uc?.candidateManualEstimation?.partnership ?? uc?.videoType;
        const breakdown = uc?.priceBreakDown;
        const labelOf = (p?: string) => (p ? advertisingTypesMap[p] ?? p : '');
        // Admin: editable partnership dropdown (restored from original), disabled
        // with a tooltip when editing isn't allowed in the current context.
        if (isAdmin && !isSibling) {
          const disabledReason = campaignEditDisabledReason(t, selectedCampaignId, uc);
          return (
            <td key={colId} className="ggc-text-caption-m">
              <InlineVideoTypeEditor
                userId={u._id}
                campaignId={selectedCampaignId ?? ''}
                initial={partnership}
                disabledReason={disabledReason}
              />
              {breakdown && breakdown.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {breakdown.map((b, i) => <AdTypeBadge key={i} typeId={b.partnership} label={labelOf(b.partnership)} />)}
                </div>
              )}
            </td>
          );
        }
        // Non-admin: read-only (breakdown chips, else partnership tag).
        if (breakdown && breakdown.length > 0) {
          return (
            <td key={colId} className="ggc-text-caption-m">
              <div className="flex flex-wrap gap-1">
                {breakdown.map((b, i) => <AdTypeBadge key={i} typeId={b.partnership} label={labelOf(b.partnership)} />)}
              </div>
            </td>
          );
        }
        return (
          <td key={colId} className="ggc-text-caption-m">
            {partnership ? <AdTypeBadge typeId={partnership} label={advertisingTypesMap[partnership] ?? partnership} /> : ED}
          </td>
        );
      }
      case 'cpvLast5': {
        const cpv = cpvEstimationByUserId?.[u._id]?.cpvFromLatestPosts;
        return (
          <td key={colId} className="ggc-text-caption-m text-right">
            {cpv != null ? `$${cpv.toFixed(2)}` : ED}
          </td>
        );
      }
      case 'cpvLastCampaign': {
        const cpvPrev = cpvEstimationByUserId?.[u._id]?.cpvFromPreviousCampaign;
        const cpvValue = typeof cpvPrev === 'number' ? cpvPrev : cpvPrev?.cpv;
        const campaignName = typeof cpvPrev === 'object' ? cpvPrev?.campaignName : undefined;
        return (
          <td key={colId} className="ggc-text-caption-m text-right">
            {cpvValue != null ? (
              <>
                <div>${cpvValue.toFixed(2)}</div>
                {campaignName && <div className="ggc-text-caption-s text-ggc-text-gray">{campaignName}</div>}
              </>
            ) : ED}
          </td>
        );
      }
      default: return <td key={colId} />;
    }
  };

  /* 사용자 상태 아이콘: 등록 여부 + 이메일 상태 */
  const statusIcons: React.ReactNode[] = [];
  if (user.isRegistered) {
    const tokenErr = user.channels?.[0]?.tokenError;
    statusIcons.push(
      <StatusIconPopover
        key="reg"
        icon={<i className="fa fa-user-circle-o" style={{ color: tokenErr ? 'var(--ggc-red)' : 'var(--ggc-green)' }} />}
        title={tokenErr ? t('Token Error', '토큰 오류') : t('Registered', 'GGContent 계정 등록됨')}
        description={tokenErr ?? t('Influencer is registered in GGContent', '인플루언서가 GGContent에 계정을 등록했습니다.')}
      />
    );
  }

  // 캠페인별 이메일 상태 아이콘
  // userCampaign.hasOpenedMessage: Sendgrid 오픈 이벤트 or 인플루언서 답장 시 true 세팅
  //   false = 발송됐으나 미오픈, true = 오픈됨, undefined = 이메일 미발송 or 데이터 없음
  const campaignMsgData = selectedCampaignId
    ? user.campaigns?.find((c) => c.campaignId === selectedCampaignId)
    : null;

  // 유효한 이메일 여부 (잘못된 이메일 주소 제외)
  const INCORRECT_EMAILS = new Set(['nocontact@gamechanger.ai', 'nocontact@ggcontent.com', 'noreply@youtube.com']);
  const userAllEmails = getInfluencerEmails(user);
  const userHasValidEmail = userAllEmails.some((e) => !INCORRECT_EMAILS.has(e) && !/@pages\.plusgoogle\.com/.test(e));

  if (selectedCampaignId && campaignMsgData) {
    // 캠페인 모드: 관리자 미열람 여부를 우선 확인 후 hasOpenedMessage 기준 아이콘
    const isAdminUnread = campaignMsgData.lastMessage?.isRead === false;
    if (isAdminUnread) {
      // 인플루언서 답장이 왔으나 관리자가 아직 읽지 않음 → 주황색
      statusIcons.push(
        <StatusIconPopover
          key="email-unread"
          icon={<i className="fa fa-envelope" style={{ color: 'var(--ggc-orange)' }} />}
          title={t('Unread Reply', '답장 미열람')}
          description={t('Influencer replied but you have not read it yet.', '인플루언서가 답장을 보냈으나 아직 열지 않았습니다.')}
        />
      );
    } else if (campaignMsgData.hasOpenedMessage === true) {
      const isReplied = campaignMsgData.lastMessage?.sentBy === 'influencer';
      const openedDate = campaignMsgData.firstOpenedAt?.slice(0, 10) ?? campaignMsgData.lastMessage?.sentAt?.slice(0, 10);
      statusIcons.push(
        <StatusIconPopover
          key="email-opened"
          icon={<i className="fa fa-envelope-open" style={{ color: 'var(--ggc-green)' }} />}
          title={isReplied ? t('Influencer Replied', '인플루언서 답장 확인') : t('Email Opened', '이메일 오픈됨')}
          description={
            isReplied
              ? t('Influencer replied.', `인플루언서가 답장했습니다.${openedDate ? ` (${openedDate})` : ''}`)
              : t('Influencer opened the email (Sendgrid tracking).', `인플루언서가 이메일을 열었습니다 — Sendgrid 오픈 이벤트.${openedDate ? ` (${openedDate})` : ''}`)
          }
        />
      );
    } else if (campaignMsgData.hasOpenedMessage === false && userHasValidEmail) {
      // 유효한 이메일이 없으면 실제 발송 안 된 것이므로 미오픈 아이콘 표시 안 함
      // 발송됐으나 미오픈 → 파란 회색 (관리자 미열람 주황색과 구분)
      statusIcons.push(
        <StatusIconPopover
          key="email-unopened"
          icon={<i className="fa fa-envelope" style={{ color: '#607d8b' }} />}
          title={t('Not Opened', '이메일 미오픈')}
          description={t('Email was sent but influencer has not opened it yet.', '이메일을 발송했으나 인플루언서가 아직 열지 않았습니다.')}
        />
      );
    }
    // hasOpenedMessage === undefined 또는 이메일 없음: 이메일 미발송 → 아이콘 없음
  } else if (!selectedCampaignId) {
    // 비캠페인 모드: 전체 hasOpenedMessage 기준
    if (user.hasOpenedMessage === false) {
      statusIcons.push(
        <StatusIconPopover
          key="msg"
          icon={<i className="fa fa-envelope" style={{ color: '#860000' }} />}
          title={t('Never Read', '메시지 미열람')}
          description={t('Influencer has never read our messages.', '인플루언서가 메시지를 한 번도 읽지 않았습니다.')}
        />
      );
    }
  }

  const rowCampaignClass = showLegendOnRow
    ? (user.isBlacklisted ? 'blacklisted' : inCampaignDeleted ? 'in-campaign-deleted' : inCampaign ? 'in-campaign' : '')
    : '';
  const rowClasses = [selected ? 'row-selected' : '']
    .concat(rowCampaignClass)
    .concat(showLegendOnRow && stripeClass ? stripeClass : '')
    .concat(showSiblings && siblings.length > 0 ? 'sibling-parent-expanded' : '')
    .filter(Boolean).join(' ') || undefined;

  const colsWithoutRowNum = visibleCols.filter((c) => c.id !== 'rowNum');
  const hasRowNum = visibleCols.some((c) => c.id === 'rowNum');

  return (
    <>
      <tr className={rowClasses}>
        {hasRowNum && renderCol('rowNum')}
        <td className="checkbox-actions-cell checkbox-actions-cell--no-actions">
          <div className="checkbox-actions-cell-inner">
            <input type="checkbox" aria-label={t('Select row', '행 선택')} checked={selected} onChange={() => onToggle(user._id)} />
          </div>
        </td>
        <td className="user-status-icons">{statusIcons}</td>
        {colsWithoutRowNum.map((c) => renderCol(c.id))}
      </tr>
      {showSiblings && siblings.map((sib, sibIdx) => {
        const sibSelected = selectedUserIds.has(sib._id);
        return (
          <tr key={`${user._id}_${sib._id}`} className={`sibling-row${sibIdx === siblings.length - 1 ? ' sibling-row--last' : ''}`}>
            {hasRowNum && renderCol('rowNum', sib, true, sibIdx)}
            <td className="checkbox-actions-cell sibling-indent checkbox-actions-cell--no-actions">
              <input type="checkbox" aria-label={t('Select row', '행 선택')} checked={sibSelected} onChange={() => onToggle(sib._id)} />
            </td>
            <td className="w-[25px] sibling-indent" />
            {colsWithoutRowNum.map((c) => renderCol(c.id, sib, true))}
          </tr>
        );
      })}
    </>
  );
}

/* ── Inline Final Price Editor ──────────────────────────────── */

type DurationUnit = 'Hour' | 'Minute';

interface PriceItem {
  id: string;
  videoType: string;
  durationFromValue: number | '';
  durationFromUnit: DurationUnit;
  durationToValue: number | '';
  durationToUnit: DurationUnit;
  finalOffer: number | '';
  advertiserPrice: number | '';
  /** True once the admin has typed their own Advertiser price for this row — stops the
   *  auto-calc-from-Final-offer in `updateItem` from overwriting it. */
  advertiserPriceManual?: boolean;
}

function makePriceItem(videoType = ''): PriceItem {
  return {
    id: Math.random().toString(36).slice(2),
    videoType,
    durationFromValue: '', durationFromUnit: 'Hour',
    durationToValue: '', durationToUnit: 'Hour',
    finalOffer: '', advertiserPrice: '',
  };
}

// Stored per-video-type price breakdown (mirrors userCampaign.priceBreakDown)
type PriceBreakDownItem = {
  partnership?: string;
  priceFinal?: number;
  priceAdvertiser?: number;
  duration?: { min?: { count?: number; unit?: string }; max?: { count?: number; unit?: string } };
};

/**
 * Per-video-type chips for the Final offer / Advertiser price / Video Type columns.
 * mode 'final'/'advertiser' → "<type>, $<price>"; mode 'type' → "<type>".
 */
function renderBreakdownChips(
  breakdown: PriceBreakDownItem[] | undefined,
  mode: 'final' | 'advertiser' | 'type',
  labelOf: (partnership?: string) => string,
): React.ReactNode {
  if (!breakdown || breakdown.length === 0) return null;
  return (
    <div className="mt-0.5 flex flex-wrap justify-end gap-1">
      {breakdown.map((b, i) => {
        const label = labelOf(b.partnership);
        if (mode === 'type') return <span key={i} className="breakdown-chip">{label}</span>;
        const price = mode === 'final' ? b.priceFinal : b.priceAdvertiser;
        return <span key={i} className="breakdown-chip">{label}, ${Number(price ?? 0).toLocaleString()}</span>;
      })}
    </div>
  );
}

/** One place an inclusion-comment token currently lives, with its raw (non-deduped)
 *  token list so a removal can compute the exact save payload. */
type CommentTargetData = { campaignId: string; label: string; isDefault: boolean; tokens: string[] };

function IncCommentCellAdmin({
  inf, userId, targets, unresolvableTokens, editCampaignId,
}: {
  /** Identity for the editor popover's thumbnail + platform icon + name header. */
  inf: Influencer;
  userId: string;
  targets: CommentTargetData[];
  /** Tokens whose only source has no writable campaignId (draft with no id). */
  unresolvableTokens?: string[];
  /** Save target for the "Edit" dialog: 'default' in the aggregate view, else the campaign. */
  editCampaignId: string;
}) {
  const { t } = useI18nText();
  // Per save-target local override (campaignId → saved comment). The ref is the source
  // of truth so back-to-back deletes from *different* chips that hit the SAME campaign
  // each read the prior one's saved result instead of a stale render closure (which
  // would silently resurrect the token deleted first); a version bump forces re-render.
  const localCommentsRef = useRef<Record<string, string | null>>({});
  const [, bumpVersion] = useReducer((n: number) => n + 1, 0);
  const applyLocal = (cid: string, comment: string | null) => {
    localCommentsRef.current = { ...localCommentsRef.current, [cid]: comment };
    bumpVersion();
  };

  const parse = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);
  const targetsByCid: Record<string, CommentTargetData> = {};
  for (const tg of targets) targetsByCid[tg.campaignId] = tg;
  // Effective (post-local-edit) raw token list for a target — raw, NOT deduped, so a
  // legit duplicate of some *other* token isn't collapsed when we filter one out.
  const effectiveRaw = (cid: string): string[] => {
    const lc = localCommentsRef.current[cid];
    return lc !== undefined ? parse(lc ?? '') : (targetsByCid[cid]?.tokens ?? []);
  };

  // token → deletable targets, plus display metadata (suggested / campaign names).
  const tokenTargets: Record<string, CommentDeleteTarget[]> = {};
  const suggestedSet = new Set<string>();
  const namesByToken: Record<string, string[]> = {};
  for (const tg of targets) {
    const seen = new Set<string>();
    for (const tk of effectiveRaw(tg.campaignId)) {
      if (seen.has(tk)) continue; // one row per (token, target)
      seen.add(tk);
      (tokenTargets[tk] ??= []).push({
        campaignId: tg.campaignId,
        label: tg.label,
        isDefault: tg.isDefault,
      });
      if (tg.isDefault) suggestedSet.add(tk);
      else (namesByToken[tk] ??= []).push(tg.label);
    }
  }

  // Tokens whose only source is an unresolvable (no-id) draft: shown, but not deletable here.
  const tokenBlockedReasons: Record<string, string> = {};
  for (const tk of unresolvableTokens ?? []) {
    if (!tokenTargets[tk]) {
      tokenBlockedReasons[tk] = t(
        'This comment belongs to a draft with no id — delete it from that campaign.',
        'id가 없는 초안에 속한 코멘트입니다 — 해당 캠페인에서 삭제하세요.',
      );
    }
  }

  // Sort keys come from the ORIGINAL targets, not the post-delete effective state —
  // otherwise deleting a target inside the chooser would change a token's own sort key,
  // reshuffle it past the 5-chip slice, and unmount its still-open popover mid-flow.
  const sortSuggested = new Set<string>();
  const sortNames: Record<string, number> = {};
  for (const tg of targets) {
    const seen = new Set<string>();
    for (const tk of tg.tokens) {
      if (seen.has(tk)) continue;
      seen.add(tk);
      if (tg.isDefault) sortSuggested.add(tk);
      else sortNames[tk] = (sortNames[tk] ?? 0) + 1;
    }
  }

  const displayTokens = Array.from(new Set([...Object.keys(tokenTargets), ...Object.keys(tokenBlockedReasons)]))
    .sort((a, b) => {
      // max(original, current): a delete can't lower a token's key (keeps it in place so
      // an open chooser doesn't get sliced off), but an editor-added token still rises to
      // its rightful spot instead of sinking behind the +N overflow.
      const sa = (sortSuggested.has(a) || suggestedSet.has(a)) ? 0 : 1;
      const sb = (sortSuggested.has(b) || suggestedSet.has(b)) ? 0 : 1;
      if (sa !== sb) return sa - sb;
      const na = Math.max(sortNames[a] ?? 0, namesByToken[a]?.length ?? 0);
      const nb = Math.max(sortNames[b] ?? 0, namesByToken[b]?.length ?? 0);
      if (na !== nb) return nb - na;
      return a.localeCompare(b);
    });

  const tokenCampaignMap: Record<string, { names: string[]; isSuggested?: boolean }> = {};
  for (const tk of displayTokens) {
    tokenCampaignMap[tk] = {
      names: (namesByToken[tk] ?? []).filter((n, i, arr) => n && arr.indexOf(n) === i),
      isSuggested: suggestedSet.has(tk),
    };
  }

  // Remove `token` from each given target: save that target's remaining tokens and
  // record the result. A per-target failure (e.g. the BE rejecting a 'default' save for
  // an admin without permission) is surfaced globally; the rest still delete.
  const onRemoveFromTargets = async (token: string, cids: string[]) => {
    for (const cid of cids) {
      const next = effectiveRaw(cid).filter((x) => x !== token);
      try {
        const saved = await saveCommentTokens(userId, next, { campaignId: cid, applyToSiblings: false });
        applyLocal(cid, saved);
      } catch { /* surfaced by the global error handler; continue with the rest */ }
    }
  };

  // The "Edit" dialog edits one target (default in the aggregate view). Surface each
  // token's memberships in *other* campaigns too — deletable in place via the same
  // chooser — so a token in both default and a campaign shows in both spots and the user
  // never has to leave the dialog. Filter the target, not the token.
  const editComment = effectiveRaw(editCampaignId).join(', ');
  const blockedTokensForEditor = displayTokens
    .map((tk) => ({ token: tk, targets: (tokenTargets[tk] ?? []).filter((tg) => tg.campaignId !== editCampaignId) }))
    .filter((b) => b.targets.length > 0);

  return (
    <>
      {displayTokens.length > 0 && (
        <IncCommentCell
          tokens={displayTokens}
          tokenCampaignMap={tokenCampaignMap}
          hideDetails
          tokenTargets={tokenTargets}
          tokenBlockedReasons={tokenBlockedReasons}
          onRemoveFromTargets={onRemoveFromTargets}
        />
      )}
      <InclusionCommentEditor
        userId={userId}
        channel={inf.channels?.[0]}
        displayName={inf.profile?.fullname || inf.profile?.name || inf.channels?.[0]?.title || t('Unknown', '알 수 없음')}
        comment={editComment}
        campaignId={editCampaignId}
        blockedTokens={blockedTokensForEditor}
        onRemoveFromTargets={onRemoveFromTargets}
        onSaved={(newComment) => applyLocal(editCampaignId, newComment)}
      />
    </>
  );
}

/**
 * Localized reason why a per-campaign editor is disabled, or null when editing
 * is allowed. Instead of hiding the editor we show it disabled with this reason
 * in a tooltip so admins understand why a value isn't editable in the current
 * context. `allowDeleted` keeps Initial offer editable on deleted influencers
 * (matches the original, which only guarded Final offer / Advertiser / Video).
 */
function campaignEditDisabledReason(
  t: (en: string, ko: string) => string,
  selectedCampaignId: string | undefined,
  uc: { status?: string } | undefined,
  opts?: { allowDeleted?: boolean },
): string | null {
  if (!selectedCampaignId || selectedCampaignId === 'all') {
    return t('Select a specific campaign to edit this value', '특정 캠페인을 선택하면 편집할 수 있습니다');
  }
  if (!uc) {
    return t('Influencer is not in the selected campaign', '선택한 캠페인에 속한 인플루언서가 아닙니다');
  }
  if (!opts?.allowDeleted && uc.status === 'deleted') {
    return t('Cannot edit a deleted influencer', '삭제된 인플루언서는 편집할 수 없습니다');
  }
  return null;
}

/**
 * Inline number editor for Initial offer / Advertiser price — restores the
 * original always-visible, save-on-Enter field. When `disabledReason` is set
 * the field is rendered disabled with the reason shown in a tooltip (instead of
 * being hidden), seeded with the current value for reference.
 */
function InlinePriceEditor({
  value, fallback, type, userId, campaignId, disabledReason, onSaved,
}: {
  value?: number;
  // Shown as the seed when no value is stored (Advertiser price defaults to
  // final price × commission rate).
  fallback?: number;
  type: 'proposal' | 'advertiser';
  userId: string;
  campaignId: string;
  disabledReason?: string | null;
  onSaved?: (value: number) => void;
}) {
  const seed = value ?? fallback;
  const [draft, setDraft] = useState<string>(seed != null ? String(seed) : '');
  const [status, setStatus] = useState<'idle' | 'saving' | 'done' | 'error'>('idle');

  // Re-seed when the stored value changes externally (e.g. campaign switch).
  useEffect(() => {
    const next = value ?? fallback;
    setDraft(next != null ? String(next) : '');
    setStatus('idle');
  }, [value, fallback]);

  if (disabledReason) {
    return (
      <div className="inline-price-editor">
        <i className='fa fa-dollar mr-0.5' />
        <Tooltip essential content={disabledReason}>
          <input
            type="number"
            className="inline-price-editor__input inline-price-editor__input--disabled"
            value={draft}
            disabled
            readOnly
          />
        </Tooltip>
      </div>
    );
  }

  const save = async () => {
    const num = Number(draft);
    if (!Number.isFinite(num) || num < 0) return;
    setStatus('saving');
    try {
      await updateInfluencerPrice({ userId, campaignId, value: num, type });
      onSaved?.(num);
      setStatus('done');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="inline-price-editor">
      <i className='fa fa-dollar mr-0.5' />
      <input
        type="number"
        className="inline-price-editor__input"
        value={draft}
        min={0}
        onChange={(e) => { setDraft(e.target.value); setStatus('idle'); }}
        onKeyDown={(e) => { if (e.key === 'Enter') void save(); }}
      />
      {status === 'saving' && <i className="fa fa-spinner fa-spin inline-price-editor__icon" />}
      {status === 'done' && <i className="fa fa-check inline-price-editor__icon inline-price-editor__icon--ok" />}
      {status === 'error' && <i className="fa fa-times inline-price-editor__icon inline-price-editor__icon--err" />}
    </div>
  );
}

/**
 * Inline partnership (video type) selector — restores the original editable
 * dropdown that wrote `candidateManualEstimation.partnership` per influencer.
 * Disabled + tooltip when editing isn't allowed in the current context.
 */
function InlineVideoTypeEditor({
  userId, campaignId, initial, disabledReason,
}: {
  userId: string;
  campaignId: string;
  initial?: string;
  disabledReason?: string | null;
}) {
  const advertisingTypes = useAppSelector((s) => s.envs.advertisingTypes) as Array<{ _id?: string; id?: string; title?: string; isObsolete?: boolean }>;
  const [value, setValue] = useState<string>(initial ?? '');

  useEffect(() => { setValue(initial ?? ''); }, [initial]);

  const options = [
    { value: '', label: '-' },
    ...advertisingTypes
      .filter((a) => !a.isObsolete)
      .map((a) => ({ value: a._id ?? a.id ?? '', label: a.title ?? '' })),
  ];

  if (disabledReason) {
    return (
      <Tooltip essential content={disabledReason}>
        <span className="inline-videotype-editor inline-videotype-editor--disabled table-inline-select">
          <SingleSelect options={options} value={value} onChange={() => {}} disabled searchable size="sm" />
        </span>
      </Tooltip>
    );
  }

  const onChange = async (v: string) => {
    setValue(v);
    try {
      await updateInfluencerVideoType({ userId, campaignId, partnership: v || null });
    } catch { /* optimistic; failure surfaced by the api layer */ }
  };

  return (
    <div className="inline-videotype-editor table-inline-select">
      <SingleSelect options={options} value={value} onChange={onChange} searchable size="sm" />
    </div>
  );
}

function FinalPriceEditor({
  userId, campaignId, disabledReason, priceBreakDown, commissionRate, currentVideoType,
  existingFinalPrice, existingAdvertiserPrice, onSaved,
}: {
  userId: string;
  campaignId: string;
  disabledReason?: string | null;
  priceBreakDown?: PriceBreakDownItem[];
  commissionRate?: number;
  currentVideoType?: string;
  existingFinalPrice?: number;
  existingAdvertiserPrice?: number;
  onSaved: (finalTotal: number | null, advertiserTotal: number | null, breakdown: PriceBreakDownItem[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState<PriceItem[]>([]);
  const { t } = useI18nText();
  const advertisingTypes = useAppSelector((s) => s.envs.advertisingTypes) as Array<{ _id?: string; id?: string; title?: string }>;
  const streamType = advertisingTypes.find((t) => t._id === 'stream' || t.id === 'stream');
  const streamId = streamType ? (streamType._id ?? streamType.id ?? '') : (advertisingTypes[0]?._id ?? advertisingTypes[0]?.id ?? '');
  // currentVideoType is used only when seeding existing prices; new empty items always default to stream
  const defaultVideoType = currentVideoType ?? streamId;

  const openDialog = useCallback(() => {
    // Seed priority:
    // 1. Stored per-type breakdown, if this session already saved one (BE has no real
    //    persistence for it yet — see the Save handler below — so on a fresh page load
    //    this is almost always empty; kept only as a same-session convenience).
    // 2. Existing aggregate price as a single row (priceFinal set but no breakdown).
    // 3. One empty row.
    // Previously also auto-seeded one row per entry in the channel's registered rate card
    // (ratesV2) — removed per feedback: for a routine single-type negotiation this buried
    // the actual Final offer under unrelated suggested types/prices pulled from the
    // channel's general rate card, not this campaign.
    if (priceBreakDown && priceBreakDown.length > 0) {
      setItems(priceBreakDown.map((bd) => ({
        id: Math.random().toString(36).slice(2),
        videoType: bd.partnership ?? defaultVideoType,
        durationFromValue: bd.duration?.min?.count ?? '',
        durationFromUnit: bd.duration?.min?.unit === 'm' ? 'Minute' : 'Hour',
        durationToValue: bd.duration?.max?.count ?? '',
        durationToUnit: bd.duration?.max?.unit === 'm' ? 'Minute' : 'Hour',
        finalOffer: bd.priceFinal ?? '',
        advertiserPrice: bd.priceAdvertiser ?? '',
        advertiserPriceManual: bd.priceAdvertiser != null && bd.priceAdvertiser > 0,
      } satisfies PriceItem)));
    } else if (existingFinalPrice && existingFinalPrice > 0) {
      const hasExistingAdvertiserPrice = existingAdvertiserPrice != null && existingAdvertiserPrice > 0;
      setItems([{
        ...makePriceItem(defaultVideoType),
        finalOffer: existingFinalPrice,
        advertiserPrice: hasExistingAdvertiserPrice
          ? existingAdvertiserPrice
          : (commissionRate ? ceilWithCommission(existingFinalPrice, commissionRate) : ''),
        advertiserPriceManual: hasExistingAdvertiserPrice,
      }]);
    } else {
      setItems([makePriceItem(defaultVideoType)]);
    }
    setOpen(true);
  }, [priceBreakDown, defaultVideoType, commissionRate, existingFinalPrice, existingAdvertiserPrice]);

  const addItem = useCallback(() => setItems((prev) => [...prev, makePriceItem(streamId)]), [streamId]);
  const removeItem = useCallback((id: string) => setItems((prev) => prev.filter((i) => i.id !== id)), []);
  const updateItem = useCallback((id: string, patch: Partial<PriceItem>) => setItems((prev) => prev.map((i) => {
    if (i.id !== id) return i;
    const next: PriceItem = { ...i, ...patch };
    if ('finalOffer' in patch && !next.advertiserPriceManual) {
      // Keep Advertiser price in sync with Final offer × commission until the admin types
      // their own Advertiser price (see resetAdvertiserPrice below for the opt back in) —
      // mirrors EstPriceCell's live ceilWithCommission recompute
      // (src/pages/influencers/components/cells/EstPriceCell.tsx:546-548). Previously a
      // manually-entered/added row's Advertiser price stayed blank, which meant the Save
      // call below skipped sending `type: 'advertiser'` entirely — and since the BE always
      // clears the stored Advertiser price whenever Final offer changes
      // (updateInfluencerPrice.action.ts), that skip left the old Advertiser price wiped
      // with nothing to restore it.
      next.advertiserPrice = next.finalOffer === ''
        ? ''
        : (commissionRate ? ceilWithCommission(Number(next.finalOffer), commissionRate) : next.advertiserPrice);
    } else if ('advertiserPrice' in patch) {
      next.advertiserPriceManual = true;
    }
    return next;
  })), [commissionRate]);
  // "Clear advertiser price" now means "go back to auto-calculating it from Final offer"
  // rather than leaving it blank — blank would just immediately re-skip the BE save call
  // this whole fix exists to prevent (see updateItem's comment above).
  const resetAdvertiserPrice = useCallback((id: string) => setItems((prev) => prev.map((i) => {
    if (i.id !== id) return i;
    const auto = i.finalOffer !== '' && commissionRate ? ceilWithCommission(Number(i.finalOffer), commissionRate) : '';
    return { ...i, advertiserPrice: auto, advertiserPriceManual: false };
  })), [commissionRate]);

  // Totals are computed as the sum of the per-type breakdown
  const totalFinal = items.reduce((s, i) => s + (i.finalOffer || 0), 0);
  const totalAdvertiser = items.reduce((s, i) => s + (i.advertiserPrice || 0), 0);

  const [saveError, setSaveError] = useState<string | null>(null);

  const save = useCallback(async () => {
    const validItems = items.filter((i) => i.videoType && Number(i.finalOffer) > 0);
    if (validItems.length === 0) {
      setSaveError(t('Please enter a price for at least one video type.', '최소 하나의 영상 타입에 가격을 입력해주세요.'));
      return;
    }
    setSaveError(null);
    setSaving(true);
    try {
      const sumFinal = validItems.reduce((s, i) => s + Number(i.finalOffer), 0);
      const sumAdvertiser = validItems.reduce((s, i) => s + (Number(i.advertiserPrice) || 0), 0);
      const breakdown: PriceBreakDownItem[] = validItems.map((i) => ({
        partnership: i.videoType,
        priceFinal: Number(i.finalOffer),
        priceAdvertiser: Number(i.advertiserPrice) || Number(i.finalOffer),
        duration: (i.durationFromValue !== '' || i.durationToValue !== '')
          ? {
              min: i.durationFromValue !== '' ? { count: Number(i.durationFromValue), unit: i.durationFromUnit === 'Minute' ? 'm' : 'h' } : undefined,
              max: i.durationToValue !== '' ? { count: Number(i.durationToValue), unit: i.durationToUnit === 'Minute' ? 'm' : 'h' } : undefined,
            }
          : undefined,
      }));
      // Save sequentially to avoid MongoDB write conflicts on the same User document
      await updateInfluencerPrice({ userId, campaignId, value: sumFinal, type: 'final' });
      if (sumAdvertiser > 0) {
        await updateInfluencerPrice({ userId, campaignId, value: sumAdvertiser, type: 'advertiser' });
      }
      try {
        // breakdown type is not yet supported by any backend endpoint; ignore failure
        await updateInfluencerPrice({ userId, campaignId, type: 'breakdown', value: breakdown }, { skipErrorToast: true });
      } catch { /* no-op */ }
      onSaved(sumFinal || null, sumAdvertiser > 0 ? sumAdvertiser : null, breakdown);
      setOpen(false);
    } catch { /* handled */ } finally {
      setSaving(false);
    }
  }, [userId, campaignId, items, onSaved, t]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  if (disabledReason) {
    return (
      <Tooltip content={disabledReason}>
        <RowActionButton disabled>
          {t('Edit', '편집')}
        </RowActionButton>
      </Tooltip>
    );
  }

  return (
    <>
      <RowActionButton onClick={openDialog}>
        {t('Edit', '편집')}
      </RowActionButton>
      {open && createPortal(
        <div
          className="fixed inset-0 z-[1500] flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.45)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="bg-white rounded-ggc-lg shadow-ggc-lg" style={{ width: 920, maxWidth: '96vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '24px 28px', overflowY: 'auto', flex: 1 }}>
              <div className="ggc-text-section-header text-ggc-text-primary mb-3">{t('Edit Final offer & Advertiser price (per video type)', 'Edit Final offer & Advertiser price (per video type)')}</div>
              {/* Column headers */}
              <div style={{ display: 'grid', gridTemplateColumns: '200px 320px 110px 1fr 32px', gap: '0 12px', paddingBottom: 10, borderBottom: '1px solid var(--ggc-border-gray)' }}>
                <div className="text-xs font-bold text-ggc-text-gray">{t('Video Type', 'Video Type')}</div>
                <div className="text-xs font-bold text-ggc-text-gray">{t('Duration', '재생시간')}</div>
                <div className="text-xs font-bold text-ggc-text-gray">{t('Final offer', 'Final offer')}</div>
                <div className="text-xs font-bold text-ggc-text-gray">{t('Advertiser price', 'Advertiser price')}</div>
                <div />
              </div>
              {/* Item rows */}
              {items.map((item) => (
                <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '200px 320px 110px 1fr 32px', gap: '0 12px', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--ggc-border-gray)' }}>
                  <GgcSelect
                    size="sm"
                    wrapperClassName="w-full"
                    value={item.videoType}
                    onChange={(e) => updateItem(item.id, { videoType: e.target.value })}
                  >
                    {advertisingTypes.map((at) => {
                      const id = at._id ?? at.id ?? '';
                      return <option key={id} value={id}>{at.title ?? id}</option>;
                    })}
                  </GgcSelect>
                  <div className="flex items-center gap-1">
                    <GgcInput
                      size="sm"
                      type="number" min={0}
                      className="w-16 text-right tabular-nums"
                      value={item.durationFromValue}
                      onChange={(e) => updateItem(item.id, { durationFromValue: e.target.value === '' ? '' : Number(e.target.value) })}
                    />
                    <GgcSelect
                      size="sm"
                      wrapperClassName="w-[80px]"
                      value={item.durationFromUnit}
                      onChange={(e) => updateItem(item.id, { durationFromUnit: e.target.value as DurationUnit })}
                    >
                      <option value="Hour">{t('Hour', 'Hour')}</option>
                      <option value="Minute">{t('Minute', 'Minute')}</option>
                    </GgcSelect>
                    <span className="text-ggc-text-gray text-sm px-0.5">–</span>
                    <GgcInput
                      size="sm"
                      type="number" min={0}
                      className="w-16 text-right tabular-nums"
                      value={item.durationToValue}
                      onChange={(e) => updateItem(item.id, { durationToValue: e.target.value === '' ? '' : Number(e.target.value) })}
                    />
                    <GgcSelect
                      size="sm"
                      wrapperClassName="w-[80px]"
                      value={item.durationToUnit}
                      onChange={(e) => updateItem(item.id, { durationToUnit: e.target.value as DurationUnit })}
                    >
                      <option value="Hour">{t('Hour', 'Hour')}</option>
                      <option value="Minute">{t('Minute', 'Minute')}</option>
                    </GgcSelect>
                  </div>
                  <div className="flex items-center bg-white overflow-hidden w-full">
                    <span className="pl-2 pr-0.5 text-sm text-ggc-text-gray select-none">$</span>
                    <input
                      type="number" min={0}
                      className="py-1.5 pr-2 text-sm text-right tabular-nums bg-white flex-1 min-w-0 outline-none w-20"
                      value={item.finalOffer}
                      onChange={(e) => updateItem(item.id, { finalOffer: e.target.value === '' ? '' : Number(e.target.value) })}
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="flex items-center bg-white overflow-hidden w-full">
                      <span className="pl-2 pr-0.5 text-sm text-ggc-text-gray select-none">$</span>
                      <input
                        type="number" min={0}
                        className="py-1.5 pr-2 text-sm text-right tabular-nums bg-white flex-1 min-w-0 outline-none w-20"
                        value={item.advertiserPrice}
                        onChange={(e) => updateItem(item.id, { advertiserPrice: e.target.value === '' ? '' : Number(e.target.value) })}
                      />
                    </div>
                    <button
                      type="button"
                      className="w-5 h-5 rounded-full bg-ggc-border-gray-dark text-white flex items-center justify-center hover:bg-ggc-text-gray flex-shrink-0"
                      title={t('Reset to auto-calculated price', '자동 계산된 가격으로 재설정')}
                      onClick={() => resetAdvertiserPrice(item.id)}
                    >
                      <i className="fa fa-times" style={{ fontSize: 8 }} />
                    </button>
                  </div>
                  <div className="flex justify-center">
                    {items.length > 1 && (
                      <button type="button"
                        className="w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"
                        onClick={() => removeItem(item.id)}
                      >
                        <i className="fa fa-minus" style={{ fontSize: 9 }} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="mt-4 border border-dashed border-ggc-border-gray-dark rounded-ggc-sm px-5 py-2 text-sm text-ggc-text-gray hover:border-ggc-main hover:text-ggc-main transition-colors"
                style={{ width: 220 }}
                onClick={addItem}
              >
                + {t('Add price item', 'Add price item')}
              </button>
            </div>
            {/* Footer */}
            <div className="border-t border-ggc-border-gray px-7 py-4 flex items-center justify-between">
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-bold text-ggc-text-primary">
                  Total: ${totalFinal.toLocaleString()} ({t('Final offer', 'Final offer')}), ${totalAdvertiser.toLocaleString()} ({t('Advertiser price', 'Advertiser price')})
                </span>
                {commissionRate != null && commissionRate > 0 && (
                  <span className="text-xs text-ggc-text-gray">
                    Commission: ×{commissionRate.toFixed(2)} ({Math.round((commissionRate - 1) * 100)}%)
                  </span>
                )}
              </div>
              <div className="flex flex-col items-end gap-1.5">
                {saveError && <span className="text-xs text-red-500">{saveError}</span>}
                <div className="flex gap-2">
                  <GgcButton variant="secondary" size="sm" onClick={() => setOpen(false)}>Cancel</GgcButton>
                  <GgcButton variant="primary" size="sm" onClick={() => void save()} disabled={saving}>
                    {saving && <i className="fa fa-spinner fa-spin mr-1" />}Save
                  </GgcButton>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
